import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { adminFetch } from '../../lib/adminFetch';
import WageDataAdmin from './WageDataAdmin';
import MinWageAdmin, { DEFAULT_MIN_WAGE } from './MinWageAdmin';

/* =========================================================================
 * レポート管理
 *   顧客管理   : 顧問先企業の登録（③人件費予実用の入力値も保持）
 *   CSV取り込み : 勤怠CSVを顧客に紐づけてサーバー保存（同月は丸ごと差し替え・無い月は蓄積）
 *   マンスリー集計 : 顧客＋月を選び、保存済みデータから集計レポート表示
 * 給与・個人情報を含むため保存先 data/ は直接アクセス遮断済み。
 * ========================================================================= */

// ---- CSVパーサ（RFC4180準拠・引用符/改行対応・BOM除去）----
function parseCSV(text) {
  if (text.charCodeAt(0) === 0xfeff) text = text.slice(1);
  const rows = [];
  let field = '', row = [], inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') { if (text[i + 1] === '"') { field += '"'; i++; } else inQuotes = false; }
      else field += c;
    } else {
      if (c === '"') inQuotes = true;
      else if (c === ',') { row.push(field); field = ''; }
      else if (c === '\r') { /* skip */ }
      else if (c === '\n') { row.push(field); rows.push(row); row = []; field = ''; }
      else field += c;
    }
  }
  if (field !== '' || row.length) { row.push(field); rows.push(row); }
  if (!rows.length) return [];
  const header = rows[0];
  return rows.slice(1)
    .filter((r) => r.some((v) => v !== ''))
    .map((r) => {
      const o = {};
      header.forEach((h, idx) => { o[h.trim()] = (r[idx] ?? '').trim(); });
      return o;
    });
}

// ---- 値ヘルパ ----
const hm = (s) => {
  if (!s) return 0;
  const p = String(s).split(':').map((x) => parseInt(x, 10));
  if (p.some(isNaN)) return 0;
  return (p[0] || 0) + (p[1] || 0) / 60 + (p[2] || 0) / 3600;
};
const num = (s) => {
  if (s === undefined || s === null || s === '') return 0;
  const n = parseFloat(String(s).replace(/,/g, ''));
  return isNaN(n) ? 0 : n;
};
const fmtH = (h) => h.toLocaleString('ja-JP', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const fmtY = (n) => Math.round(n).toLocaleString('ja-JP');
const monthKey = (s) => { const m = String(s).match(/(\d{4})\D+(\d{1,2})/); return m ? `${m[1]}-${String(m[2]).padStart(2, '0')}` : s; };
const monthLabel = (s) => { const m = String(s).match(/(\d{4})\D+(\d{1,2})/); return m ? `${m[1]}年${m[2]}月` : s; };

const tenure = (hireDate, ym) => {
  if (!hireDate) return '-';
  const h2 = String(hireDate).match(/(\d{4})\D+(\d{1,2})\D+(\d{1,2})/);
  const tm = String(ym).match(/(\d{4})\D+(\d{1,2})/);
  if (!h2 || !tm) return '-';
  let months = (parseInt(tm[1]) - parseInt(h2[1])) * 12 + (parseInt(tm[2]) - parseInt(h2[2]));
  if (months < 0) months = 0;
  return `${Math.floor(months / 12)}年${months % 12}か月`;
};
const ageOf = (birth, ym) => {
  const bm = String(birth).match(/(\d{4})\D+(\d{1,2})\D+(\d{1,2})/);
  const tm = String(ym).match(/(\d{4})\D+(\d{1,2})/);
  if (!bm || !tm) return null;
  let age = parseInt(tm[1]) - parseInt(bm[1]);
  if (parseInt(tm[2]) < parseInt(bm[2])) age--;
  return age;
};
const fullName = (r) => `${r['氏名（姓）'] || ''} ${r['氏名（名）'] || ''}`.trim();
const monthlyBase = (r) => {
  const t = r['基本給の種別'] || '';
  if (t.includes('月給')) return num(r['基本給（月給）']);
  if (t.includes('日給')) return num(r['基本給（日給）']) * num(r['所定労働日数']);
  if (t.includes('時給')) return num(r['基本給（時給）']) * num(r['月平均所定労働時間']);
  if (t.includes('年俸')) return num(r['基本給（年俸制）']) / 12;
  return num(r['基本給（月給）']) || num(r['基本給（年俸制）']) / 12
    || num(r['基本給（日給）']) * num(r['所定労働日数'])
    || num(r['基本給（時給）']) * num(r['月平均所定労働時間']);
};
const allowanceTotal = (r) => { let s = 0; for (let i = 1; i <= 21; i++) s += num(r[`手当${i}の金額（月額）`]); return s; };
// 基準内（「内」）手当の月額換算（時給×月平均所定／日額÷所定労働時間/日×月平均所定／月額そのまま）
const kijunNaiMonthly = (r) => {
  if (!r) return 0;
  const mph = num(r['月平均所定労働時間']);
  const hpd = hm(r['所定労働時間/日']);
  let s = 0;
  for (let i = 1; i <= 21; i++) {
    if (String(r[`手当${i}の基準内基準外の別`] || '').trim() !== '内') continue;
    const t = num(r[`手当${i}の金額（時給）`]); if (t) s += t * mph;
    const dd = num(r[`手当${i}の金額（日額）`]); if (dd) s += hpd > 0 ? dd / hpd * mph : 0;
    const mm = num(r[`手当${i}の金額（月額）`]); if (mm) s += mm;
  }
  return s;
};
// 基準外賃金（割増賃金）：勤怠の時間×割増率から概算。算定基礎時給＝（基本給＋基準内手当月額）÷月平均所定労働時間
const overtimePay = (r) => {
  const mph = num(r['月平均所定労働時間']);
  if (mph <= 0) return 0;
  let kijunNai = 0;
  for (let i = 1; i <= 21; i++) { if (String(r[`手当${i}の基準内基準外の別`] || '').trim() === '内') kijunNai += num(r[`手当${i}の金額（月額）`]); }
  const hourly = (monthlyBase(r) + kijunNai) / mph;
  const r1 = num(r['所定時間外労働割増率（%）']) || 100;
  const r2 = num(r['法定時間外労働割増率（月０～36協定範囲内）（%）']) || 125;
  const rH = num(r['法定休日労働割増率（%）']) || 135;
  const rN = num(r['深夜労働割増率（%）']) || 25;
  return hourly * (
    hm(r['所定時間外労働']) * r1 / 100 +
    hm(r['法定時間外労働']) * r2 / 100 +
    hm(r['法定休日労働']) * rH / 100 +
    hm(r['深夜労働']) * rN / 100
  );
};
const schedOT = (r) => hm(r['所定時間外労働']);   // 所定時間外労働（法定内残業）
const legalOT = (r) => hm(r['法定時間外労働']);   // 法定外時間外労働
const regularWork = (r) => hm(r['所定時間労働']); // 所定時間内労働

// ---- 印刷ヘルパ（非表示iframe方式）----
// window.open だとポップアップブロックで印刷できない環境があるため、
// ページ内に不可視のiframeを作ってそこへレポートHTMLを書き込み印刷する。
function printHtml(html, delay = 350) {
  const frame = document.createElement('iframe');
  frame.setAttribute('aria-hidden', 'true');
  frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
  document.body.appendChild(frame);
  const win = frame.contentWindow;
  const doc = win.document;
  doc.open(); doc.write(html); doc.close();
  const remove = () => { if (frame.parentNode) frame.parentNode.removeChild(frame); };
  win.addEventListener('afterprint', () => setTimeout(remove, 100));
  setTimeout(() => { win.focus(); win.print(); }, delay); // レイアウト確定（CSS読込）を待って印刷
  setTimeout(remove, 300000);                             // afterprint 不発時の保険
}

// CSV要約（取込プレビュー用）
function summarizeRows(rows) {
  const months = [...new Set(rows.map((r) => r['年月']))].sort((a, b) => monthKey(a).localeCompare(monthKey(b)));
  const emps = [...new Set(rows.map((r) => fullName(r)))];
  const companies = [...new Set(rows.map((r) => r['企業名']).filter(Boolean))];
  return { months, emps, companies };
}

// ====================== マンスリー集計レポート（描画） ======================
const MonthlyReport = ({ rows, enrollmentHours }) => {
  const months = useMemo(
    () => [...new Set(rows.map((r) => r['年月']))].sort((a, b) => monthKey(b).localeCompare(monthKey(a))),
    [rows]
  );
  const defaultMonth = useMemo(() => {
    for (const m of months) {
      if (rows.some((r) => r['年月'] === m && (hm(r['総労働時間数']) > 0 || num(r['出勤日数']) > 0))) return m;
    }
    return months[0];
  }, [months, rows]);
  const [sel, setSel] = useState(defaultMonth);
  useEffect(() => { setSel(defaultMonth); }, [defaultMonth]);

  // 対象月の選択補助（◀▶で月送り／年送り）
  const monthsAsc = useMemo(() => [...months].sort((a, b) => monthKey(a).localeCompare(monthKey(b))), [months]);
  const selIdx = monthsAsc.indexOf(sel);
  const goRel = (delta) => { const i = selIdx + delta; if (i >= 0 && i < monthsAsc.length) setSel(monthsAsc[i]); };
  const selYear = (monthKey(sel).match(/^(\d{4})/) || [])[1] || '';
  const selMon = (monthKey(sel).match(/-(\d{2})$/) || [])[1] || '';
  const yearTargets = (delta) => monthsAsc.filter((m) => monthKey(m).startsWith(String(parseInt(selYear, 10) + delta) + '-'));
  const goYear = (delta) => {
    const ty = String(parseInt(selYear, 10) + delta);
    const cand = monthsAsc.find((m) => monthKey(m) === `${ty}-${selMon}`) || yearTargets(delta).slice(-1)[0];
    if (cand) setSel(cand);
  };
  const stepBtn = 'px-2 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 text-sm disabled:opacity-40 disabled:hover:bg-gray-100';

  const data = useMemo(() => {
    const cur = rows.filter((r) => r['年月'] === sel);
    if (!cur.length) return null;
    const company = cur[0]['企業名'] || '—';
    const offices = {};
    cur.forEach((r) => {
      const o = r['事業所名'] || '（事業所未設定）';
      const e = r['雇用形態'] || '（雇用形態未設定）';
      offices[o] = offices[o] || { name: o, types: {}, count: 0, total: 0, reg: 0, sot: 0, lot: 0, hol: 0 };
      offices[o].types[e] = offices[o].types[e] || { type: e, count: 0, total: 0, reg: 0, sot: 0, lot: 0, hol: 0 };
      const t = offices[o].types[e];
      const tot = hm(r['総労働時間数']), reg = regularWork(r), sot = schedOT(r), lot = legalOT(r), hol = hm(r['法定休日労働']);
      t.count++; t.total += tot; t.reg += reg; t.sot += sot; t.lot += lot; t.hol += hol;
      offices[o].count++; offices[o].total += tot; offices[o].reg += reg; offices[o].sot += sot; offices[o].lot += lot; offices[o].hol += hol;
    });
    const totEmp = cur.length;
    const totHours = cur.reduce((s, r) => s + hm(r['総労働時間数']), 0);
    const totRegular = cur.reduce((s, r) => s + regularWork(r), 0);
    const totSchedOT = cur.reduce((s, r) => s + schedOT(r), 0);
    const totLegalOT = cur.reduce((s, r) => s + legalOT(r), 0);
    const totHol = cur.reduce((s, r) => s + hm(r['法定休日労働']), 0);

    // 平均年齢・平均勤続年数・正社員比率
    const ages = cur.map((r) => ageOf(r['生年月日'], sel)).filter((a) => a != null);
    const avgAge = ages.length ? ages.reduce((s, a) => s + a, 0) / ages.length : null;
    const tenures = cur.map((r) => {
      const h = String(r['入社日'] || '').match(/(\d{4})\D+(\d{1,2})/);
      const t = String(sel).match(/(\d{4})\D+(\d{1,2})/);
      if (!h || !t) return null;
      const m = (parseInt(t[1]) - parseInt(h[1])) * 12 + (parseInt(t[2]) - parseInt(h[2]));
      return m < 0 ? 0 : m / 12;
    }).filter((x) => x != null);
    const avgTenure = tenures.length ? tenures.reduce((s, a) => s + a, 0) / tenures.length : null;
    const seishainCount = cur.filter((r) => String(r['雇用形態'] || '').includes('正社員')).length;
    const seishainRate = totEmp ? (seishainCount / totEmp) * 100 : 0;

    // 男女構成・年齢ピラミッド（10歳刻み）
    const genderOf = (r) => { const g = String(r['性別'] || '').trim(); return g === '男性' ? 'male' : g === '女性' ? 'female' : 'other'; };
    const genderCounts = { male: 0, female: 0, other: 0 };
    cur.forEach((r) => { genderCounts[genderOf(r)]++; });
    const PYRAMID_BANDS = ['20歳未満', '20代', '30代', '40代', '50代', '60代', '70代以上'];
    const bandOf = (age) => { if (age == null) return null; if (age < 20) return '20歳未満'; if (age >= 70) return '70代以上'; return `${Math.floor(age / 10) * 10}代`; };
    const pyramid = PYRAMID_BANDS.map((b) => ({ band: b, male: 0, female: 0 }));
    cur.forEach((r) => {
      const b = bandOf(ageOf(r['生年月日'], sel)); if (!b) return;
      const g = genderOf(r); const row = pyramid.find((x) => x.band === b);
      if (row && (g === 'male' || g === 'female')) row[g]++;
    });
    // 「1歳刻み」の人員構成（年齢別人数のヒストグラム）
    const ageList = cur.map((r) => ageOf(r['生年月日'], sel)).filter((a) => a != null);
    const ageHist = [];
    if (ageList.length) {
      const minA = Math.min(...ageList), maxA = Math.max(...ageList);
      for (let a = minA; a <= maxA; a++) ageHist.push({ age: a, count: ageList.filter((x) => x === a).length });
    }

    const ck = monthKey(sel);
    const agreement = cur.map((r) => {
      const start = r['対象期間（開始）'], end = r['対象期間（終了）'];
      const sk = start ? monthKey(start) : null, ek = end ? monthKey(end) : null;
      const name = fullName(r);
      const cumRows = rows.filter((x) => fullName(x) === name && monthKey(x['年月']) <= ck
        && (!sk || monthKey(x['年月']) >= sk) && (!ek || monthKey(x['年月']) <= ek));
      const cumOT = cumRows.reduce((s, x) => s + hm(x['法定時間外労働']), 0);
      const capM = num(r['36協定上限（月）']) || 45;
      const cumSpecial = cumRows.filter((x) => hm(x['法定時間外労働']) > capM).length;
      return {
        name, monthOT: hm(r['法定時間外労働']), capMonth: capM, cumOT,
        capYear: num(r['36協定上限（年）']) || 360,
        specialTimes: num(r['特別条項上限（回数）']) || 6, specialUsed: cumSpecial,
      };
    });

    const alerts = [];
    agreement.forEach((a) => {
      if (a.monthOT > a.capMonth) alerts.push({ level: 'high', text: `${a.name}：当月の法定時間外が ${fmtH(a.monthOT)}h で月上限 ${a.capMonth}h を超過しています。` });
      else if (a.monthOT >= a.capMonth * 0.8) alerts.push({ level: 'warn', text: `${a.name}：当月の法定時間外が ${fmtH(a.monthOT)}h（月上限 ${a.capMonth}h の80%超）です。` });
      if (a.capYear > 0 && a.cumOT >= a.capYear * 0.8) alerts.push({ level: a.cumOT > a.capYear ? 'high' : 'warn', text: `${a.name}：年間累計の法定時間外が ${fmtH(a.cumOT)}h（年上限 ${a.capYear}h の${a.cumOT > a.capYear ? '超過' : '80%超'}）です。` });
    });

    // 社保・雇用保険の有無は月により空欄のことがあるため、全期間で最新の非空値を採用する
    const insMap = {};
    rows.forEach((r) => {
      const k = fullName(r); const mk = monthKey(r['年月']);
      const ko = String(r['厚生年金保険の有無'] || '').trim();
      const ky = String(r['雇用保険の有無'] || '').trim();
      if (!insMap[k]) insMap[k] = { kosei: '', koseiMk: '', koyou: '', koyouMk: '' };
      if (ko !== '' && mk >= insMap[k].koseiMk) { insMap[k].kosei = ko; insMap[k].koseiMk = mk; }
      if (ky !== '' && mk >= insMap[k].koyouMk) { insMap[k].koyou = ky; insMap[k].koyouMk = mk; }
    });
    const persons = cur.map((r) => ({
      name: fullName(r), office: r['事業所名'], total: hm(r['総労働時間数']),
      regular: regularWork(r), sot: schedOT(r), lot: legalOT(r), hol: hm(r['法定休日労働']),
      night: hm(r['深夜労働']), attend: num(r['出勤日数']), scheduled: num(r['所定労働日数']),
      kosei: (insMap[fullName(r)] || {}).kosei === '有', // 厚生年金保険の有無（CSV AE・全期間最新）
      koyou: (insMap[fullName(r)] || {}).koyou === '有', // 雇用保険の有無（CSV AG・全期間最新）
    })).sort((a, b) => b.total - a.total);

    const conditions = cur.map((r) => ({
      name: fullName(r), tenure: tenure(r['入社日'], sel), age: ageOf(r['生年月日'], sel),
      type: r['雇用形態'], wageType: r['基本給の種別'],
      monthlyScheduled: num(r['月平均所定労働時間']), dailyScheduled: hm(r['所定労働時間/日']),
      base: monthlyBase(r), allowance: allowanceTotal(r),
    }));
    const wageRank = conditions.map((c) => ({ ...c, total: c.base + c.allowance })).sort((a, b) => b.total - a.total);
    // 時給（基本給＋基準内手当）：
    //   時給者 … CSVのAC列「基本給（時給）」が基準内手当織込み済みの値なのでそのまま採用（手当を足すと二重加算）
    //   月給・日給・年俸者 … （基本給換算＋基準内手当）÷月平均所定労働時間
    const hourlyOf = (r) => {
      if (String(r['基本給の種別'] || '').includes('時給')) return num(r['基本給（時給）']);
      const mph = num(r['月平均所定労働時間']);
      return mph > 0 ? seiteinaiMonthly(r) / mph : 0;
    };
    const otRate = cur.map((r) => {
      // 割増率はCSVで全体倍率（125＝×1.25）として入っている（overtimePayと同じ扱い）
      const rate = num(r['法定時間外労働割増率（月０～36協定範囲内）（%）']) || num(r['所定時間外労働割増率（%）']) || 125;
      return { name: fullName(r), unit: hourlyOf(r) * rate / 100, rate };
    }).filter((x) => x.unit > 0).sort((a, b) => b.unit - a.unit);
    const wageHourly = cur.map((r) => ({ name: fullName(r), hourly: hourlyOf(r) }))
      .filter((x) => x.hourly > 0).sort((a, b) => b.hourly - a.hourly);

    return { company, offices: Object.values(offices), totEmp, totHours, totRegular, totSchedOT, totLegalOT, totHol, avgAge, avgTenure, seishainRate, genderCounts, pyramid, ageHist, agreement, alerts, persons, conditions, wageRank, otRate, wageHourly };
  }, [rows, sel]);

  if (!data) return <div className="text-gray-500">この月のデータがありません。</div>;

  // 個人別 労働時間グラフ用：総労働時間を1本の横棒にし、内訳を色分け積み上げ。
  // ※深夜労働は他区分と時間帯が重複するため内訳には含めない。
  const STACK_CATS = [
    { key: 'regular', label: '所定内', color: '#9ca3af' },
    { key: 'sot', label: '所定外', color: '#d97706' },
    { key: 'lot', label: '法定外', color: '#b45309' },
    { key: 'hol', label: '休日', color: '#e11d48' },
  ];
  // 加入基準線：歴日数（対象月の暦日数）÷ 7 × 週あたり基準時間。総労働時間と同じ時間軸に引く。
  //  ・社会保険：加入基準時間（顧客管理で設定）　・雇用保険：週20時間（固定）
  const _ym = monthKey(sel).match(/^(\d{4})-(\d{2})/);
  const daysInMonth = _ym ? new Date(Number(_ym[1]), Number(_ym[2]), 0).getDate() : 30;
  const weeksInMonth = daysInMonth / 7;
  const shakaiHours = Number(enrollmentHours) || 0;
  const shakaiThreshold = shakaiHours > 0 ? weeksInMonth * shakaiHours : 0; // 社会保険
  const koyouThreshold = weeksInMonth * 20; // 雇用保険（週20時間・固定）
  const barMax = Math.max(1, shakaiThreshold, koyouThreshold, ...data.persons.map((p) => p.total));

  const KPI = ({ label, value, unit, accent }) => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 px-5 py-4">
      <div className="text-xs text-gray-500 mb-1">{label}</div>
      <div className={`text-2xl font-bold ${accent || 'text-gray-800'}`}>{value}<span className="text-sm font-normal text-gray-400 ml-1">{unit}</span></div>
    </div>
  );
  const Section = ({ title, children, note }) => (
    <section className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
      <div className="flex items-baseline justify-between mb-4 border-b border-gray-100 pb-2">
        <h3 className="text-base font-bold text-gray-800">{title}</h3>
        {note && <span className="text-[11px] text-gray-400">{note}</span>}
      </div>
      {children}
    </section>
  );

  return (
    <div>
      <div className="flex flex-wrap items-center gap-x-5 gap-y-3 mb-6">
        <div className="flex items-center gap-1">
          <label className="text-xs text-gray-500 mr-1">対象月</label>
          <button onClick={() => goRel(-1)} disabled={selIdx <= 0} className={stepBtn} title="前の月">◀</button>
          <select value={sel} onChange={(e) => setSel(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-dark">
            {months.map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)}
          </select>
          <button onClick={() => goRel(1)} disabled={selIdx < 0 || selIdx >= monthsAsc.length - 1} className={stepBtn} title="次の月">▶</button>
        </div>
        <div className="flex items-center gap-1">
          <span className="text-xs text-gray-500 mr-1">年</span>
          <button onClick={() => goYear(-1)} disabled={yearTargets(-1).length === 0} className={stepBtn} title="前の年">◀</button>
          <span className="w-14 text-center text-sm font-bold text-gray-800">{selYear}年</span>
          <button onClick={() => goYear(1)} disabled={yearTargets(1).length === 0} className={stepBtn} title="次の年">▶</button>
        </div>
        <button onClick={() => window.print()} className="ml-auto bg-pink-dark text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-brown">PDF / 印刷</button>
      </div>

      <div id="report-print" className="bg-gray-50 rounded-2xl p-6">
        <div className="mb-6 rounded-2xl bg-pink-dark text-white px-8 py-6 shadow">
          <div className="text-sm opacity-80">{monthLabel(sel)}度</div>
          <h2 className="text-2xl font-bold mt-1">マンスリー分析レポート</h2>
          <div className="mt-3 text-sm opacity-90">{data.company}</div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <KPI label="対象従業員数" value={data.totEmp} unit="名" accent="text-pink-dark" />
          <KPI label="平均年齢" value={data.avgAge != null ? fmtH(data.avgAge) : '—'} unit="歳" accent="text-pink-dark" />
          <KPI label="平均勤続年数" value={data.avgTenure != null ? fmtH(data.avgTenure) : '—'} unit="年" accent="text-pink-dark" />
          <KPI label="正社員比率" value={Math.round(data.seishainRate)} unit="%" accent="text-pink-dark" />
          <KPI label="総労働時間" value={fmtH(data.totHours)} unit="h" />
          <KPI label="平均総労働時間" value={fmtH(data.totEmp ? data.totHours / data.totEmp : 0)} unit="h" />
          <KPI label="所定時間内労働" value={fmtH(data.totRegular)} unit="h" />
          <KPI label="所定時間外労働" value={fmtH(data.totSchedOT)} unit="h" accent="text-amber-600" />
          <KPI label="法定外時間外労働" value={fmtH(data.totLegalOT)} unit="h" accent="text-amber-700" />
          <KPI label="総法定休日労働" value={fmtH(data.totHol)} unit="h" accent="text-rose-600" />
        </div>

        <Section title="連絡事項（アラート）" note="残業・36協定の状況から自動判定">
          {data.alerts.length === 0
            ? <div className="text-sm text-gray-500">⚠️ 警告対象はありません。</div>
            : <ul className="space-y-2">
                {data.alerts.map((a, i) => (
                  <li key={i} className={`text-sm rounded-lg px-4 py-2 ${a.level === 'high' ? 'bg-rose-50 text-rose-700' : 'bg-amber-50 text-amber-700'}`}>
                    {a.level === 'high' ? '🔴' : '🟡'} {a.text}
                  </li>
                ))}
              </ul>}
        </Section>

        <Section title="人数構成" note={`${monthLabel(sel)}末時点`}>
          <div className="space-y-3">
            {data.offices.map((o) => (
              <div key={o.name} className="flex flex-wrap items-center gap-2">
                <span className="font-bold text-gray-700 w-40 shrink-0">{o.name}</span>
                <div className="flex flex-wrap gap-2">
                  {Object.values(o.types).map((t) => (
                    <span key={t.type} className="text-xs bg-pink-beige/40 text-brown rounded-full px-3 py-1">{t.type}（{t.count}）</span>
                  ))}
                </div>
                <span className="ml-auto font-bold text-gray-800">{o.count}名</span>
              </div>
            ))}
          </div>
        </Section>

        <Section title="男女構成・年齢ピラミッド" note={`${monthLabel(sel)}時点`}>
          {(() => {
            const g = data.genderCounts; const totG = (g.male + g.female + g.other) || 1;
            const pyMax = Math.max(1, ...data.pyramid.flatMap((r) => [r.male, r.female]));
            const pct = (n) => Math.round(n / totG * 100);
            return (
              <div className="space-y-6">
                <div>
                  <div className="text-xs text-gray-500 mb-1">男女構成比</div>
                  <div className="flex h-7 rounded-lg overflow-hidden text-[11px] font-bold text-white">
                    {g.male > 0 && <div className="bg-blue-600 flex items-center justify-center" style={{ width: `${g.male / totG * 100}%` }}>男 {g.male}</div>}
                    {g.female > 0 && <div className="bg-pink-500 flex items-center justify-center" style={{ width: `${g.female / totG * 100}%` }}>女 {g.female}</div>}
                    {g.other > 0 && <div className="bg-gray-400 flex items-center justify-center" style={{ width: `${g.other / totG * 100}%` }}>不明 {g.other}</div>}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">男性 {pct(g.male)}％・女性 {pct(g.female)}％{g.other ? `・不明 ${pct(g.other)}％` : ''}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-2">年齢別ピラミッド（<span className="text-blue-600 font-bold">男性</span> ／ <span className="text-pink-500 font-bold">女性</span>）</div>
                  <div className="space-y-1">
                    {[...data.pyramid].reverse().map((row) => (
                      <div key={row.band} className="flex items-center gap-2 text-xs">
                        <div className="flex-1 flex items-center justify-end gap-1">
                          <span className="text-gray-400 w-5 text-right">{row.male || ''}</span>
                          <div className="bg-blue-600 h-4 rounded-l-sm" style={{ width: `${row.male / pyMax * 100}%` }} />
                        </div>
                        <div className="w-16 text-center text-gray-600 shrink-0">{row.band}</div>
                        <div className="flex-1 flex items-center justify-start gap-1">
                          <div className="bg-pink-500 h-4 rounded-r-sm" style={{ width: `${row.female / pyMax * 100}%` }} />
                          <span className="text-gray-400 w-5">{row.female || ''}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })()}
        </Section>

        <Section title="「1歳刻み」の人員構成" note={`${monthLabel(sel)}時点・年齢別人数`}>
          {data.ageHist.length === 0 ? <div className="text-sm text-gray-400">生年月日データがありません。</div> : (() => {
            const W = 900, H = 240, padL = 34, padR = 10, padT = 12, padB = 26;
            const plotW = W - padL - padR, plotH = H - padT - padB;
            const n = data.ageHist.length;
            const yMax = Math.max(1, ...data.ageHist.map((h) => h.count));
            const bw = plotW / n;
            const yPos = (v) => padT + plotH - (v / yMax) * plotH;
            const yTicks = []; const yStep = yMax <= 10 ? 2 : Math.ceil(yMax / 5);
            for (let t = 0; t <= yMax; t += yStep) yTicks.push(t);
            return (
              <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ maxWidth: '100%' }}>
                {yTicks.map((t) => (
                  <g key={t}>
                    <line x1={padL} y1={yPos(t)} x2={W - padR} y2={yPos(t)} stroke="#eef2f7" />
                    <text x={padL - 4} y={yPos(t) + 3} fontSize="9" fill="#9ca3af" textAnchor="end">{t}</text>
                  </g>
                ))}
                <line x1={padL} y1={padT + plotH} x2={W - padR} y2={padT + plotH} stroke="#d1d5db" />
                {data.ageHist.map((h, i) => {
                  const x = padL + bw * i;
                  const hh = (h.count / yMax) * plotH;
                  return (
                    <g key={h.age}>
                      {h.count > 0 && <rect x={x + bw * 0.1} y={padT + plotH - hh} width={Math.max(1, bw * 0.8)} height={hh} fill="#6366f1"><title>{`${h.age}歳：${h.count}人`}</title></rect>}
                      {h.age % 5 === 0 && <text x={x + bw / 2} y={H - padB + 14} fontSize="9" fill="#6b7280" textAnchor="middle">{h.age}</text>}
                    </g>
                  );
                })}
                <text x={padL + plotW / 2} y={H - 2} fontSize="9" fill="#6b7280" textAnchor="middle">年齢（歳）</text>
              </svg>
            );
          })()}
        </Section>

        <Section title="労働時間サマリー（事業所・雇用形態別）">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b border-gray-200">
                <th className="py-2">事業所</th><th>雇用形態</th><th className="text-right">人数</th>
                <th className="text-right">総労働</th><th className="text-right">平均</th>
                <th className="text-right">所定内</th><th className="text-right">所定外</th><th className="text-right">法定外</th>
              </tr>
            </thead>
            <tbody>
              {data.offices.flatMap((o) => Object.values(o.types).map((t, i) => (
                <tr key={o.name + t.type} className="border-b border-gray-50">
                  <td className="py-2 text-gray-600">{i === 0 ? o.name : ''}</td>
                  <td>{t.type}</td><td className="text-right">{t.count}</td>
                  <td className="text-right">{fmtH(t.total)}h</td>
                  <td className="text-right text-gray-500">{fmtH(t.count ? t.total / t.count : 0)}h</td>
                  <td className="text-right text-gray-500">{fmtH(t.reg)}h</td>
                  <td className="text-right text-amber-600">{fmtH(t.sot)}h</td>
                  <td className="text-right text-amber-700">{fmtH(t.lot)}h</td>
                </tr>
              )))}
            </tbody>
          </table>
        </Section>

        <Section title="個人別 労働時間">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b border-gray-200">
                <th className="py-2">氏名</th><th>事業所</th><th className="text-right">出勤/所定</th>
                <th className="text-right">総労働</th><th className="text-right">所定内</th>
                <th className="text-right">所定外</th><th className="text-right">法定外</th><th className="text-right">休日</th><th className="text-right">深夜</th>
              </tr>
            </thead>
            <tbody>
              {data.persons.map((p) => (
                <tr key={p.name} className="border-b border-gray-50">
                  <td className="py-2 font-medium text-gray-700">{p.name}</td>
                  <td className="text-gray-500">{p.office}</td>
                  <td className="text-right">{p.attend}/{p.scheduled}</td>
                  <td className="text-right font-medium">{fmtH(p.total)}h</td>
                  <td className="text-right text-gray-500">{fmtH(p.regular)}h</td>
                  <td className="text-right text-amber-600">{fmtH(p.sot)}h</td>
                  <td className="text-right text-amber-700">{fmtH(p.lot)}h</td>
                  <td className="text-right text-rose-600">{fmtH(p.hol)}h</td>
                  <td className="text-right text-gray-500">{fmtH(p.night)}h</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>

        <Section title="個人別 労働時間グラフ" note="総労働時間＝所定内＋所定外＋法定外＋休日（全員共通スケール）">
          <div className="flex flex-wrap gap-3 mb-3 text-[11px] text-gray-600">
            {STACK_CATS.map((c) => (
              <span key={c.key} className="flex items-center gap-1">
                <span className="inline-block w-3 h-3 rounded-sm" style={{ background: c.color }} />{c.label}
              </span>
            ))}
            {shakaiThreshold > 0 && (
              <span className="flex items-center gap-1 text-blue-700">
                <span className="inline-block w-0.5 h-3" style={{ background: '#2563eb' }} />
                社保加入基準線 {fmtH(shakaiThreshold)}h（{daysInMonth}日 ÷ 7 × {shakaiHours}時間）
              </span>
            )}
            <span className="flex items-center gap-1 text-emerald-700">
              <span className="inline-block w-0.5 h-3" style={{ background: '#059669' }} />
              雇用保険加入基準線 {fmtH(koyouThreshold)}h（{daysInMonth}日 ÷ 7 × 20時間）
            </span>
            <span className="flex items-center gap-1 text-gray-500">
              <span className="text-[9px] leading-none px-1 py-0.5 rounded bg-blue-100 text-blue-700">社保</span>
              <span className="text-[9px] leading-none px-1 py-0.5 rounded bg-emerald-100 text-emerald-700">雇用</span>
              ＝加入（グレーは未加入）
            </span>
          </div>
          {shakaiThreshold <= 0 && <p className="text-[11px] text-gray-400 mb-3">※ 顧客管理で「社保加入基準時間」を設定すると、社保加入基準線（歴日数÷7×社保加入基準時間）も表示されます。</p>}
          <div className="space-y-2">
            {data.persons.map((p) => (
              <div key={p.name} className="flex items-center gap-2 text-sm">
                <div className="w-44 shrink-0 flex items-center gap-1 min-w-0">
                  <span className="truncate font-medium text-gray-700" title={p.name}>{p.name}</span>
                  <span className={`shrink-0 text-[9px] leading-none px-1 py-0.5 rounded ${p.kosei ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-400'}`} title="厚生年金保険の有無">社保</span>
                  <span className={`shrink-0 text-[9px] leading-none px-1 py-0.5 rounded ${p.koyou ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-400'}`} title="雇用保険の有無">雇用</span>
                </div>
                <div className="relative flex-1 bg-gray-100 rounded h-4 overflow-hidden">
                  <div className="flex h-4" style={{ width: `${(p.total / barMax) * 100}%` }}>
                    {STACK_CATS.map((c) => (
                      p[c.key] > 0 ? (
                        <div key={c.key} style={{ width: `${(p[c.key] / p.total) * 100}%`, background: c.color }}
                          title={`${c.label} ${fmtH(p[c.key])}h`} />
                      ) : null
                    ))}
                  </div>
                  {shakaiThreshold > 0 && (
                    <div className="absolute top-0 bottom-0 w-0.5 z-10" style={{ left: `${(shakaiThreshold / barMax) * 100}%`, background: '#2563eb' }}
                      title={`社保加入基準線 ${fmtH(shakaiThreshold)}h`} />
                  )}
                  <div className="absolute top-0 bottom-0 w-0.5 z-10" style={{ left: `${(koyouThreshold / barMax) * 100}%`, background: '#059669' }}
                    title={`雇用保険加入基準線 ${fmtH(koyouThreshold)}h`} />
                </div>
                <span className="w-14 text-right text-[11px] text-gray-600 shrink-0">{fmtH(p.total)}h</span>
              </div>
            ))}
          </div>
        </Section>

        <Section title="残業時間上限規制の確認（36協定）" note="法定時間外労働ベース">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b border-gray-200">
                <th className="py-2">氏名</th><th className="text-right">当月時間外</th><th className="text-right">月上限</th>
                <th className="text-right">年間累計</th><th className="text-right">年上限</th><th className="text-right">特別条項</th><th>判定</th>
              </tr>
            </thead>
            <tbody>
              {data.agreement.map((a) => {
                const overMonth = a.monthOT > a.capMonth, overYear = a.cumOT > a.capYear;
                return (
                  <tr key={a.name} className="border-b border-gray-50">
                    <td className="py-2 font-medium text-gray-700">{a.name}</td>
                    <td className={`text-right ${overMonth ? 'text-rose-600 font-bold' : ''}`}>{fmtH(a.monthOT)}h</td>
                    <td className="text-right text-gray-400">{a.capMonth}h</td>
                    <td className={`text-right ${overYear ? 'text-rose-600 font-bold' : ''}`}>{fmtH(a.cumOT)}h</td>
                    <td className="text-right text-gray-400">{a.capYear}h</td>
                    <td className="text-right text-gray-600">{a.specialUsed}/{a.specialTimes}回</td>
                    <td>{overMonth || overYear
                      ? <span className="text-rose-600 font-bold">超過</span>
                      : (a.cumOT >= a.capYear * 0.8 || a.monthOT >= a.capMonth * 0.8)
                        ? <span className="text-amber-600">注意</span>
                        : <span className="text-emerald-600">適正</span>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Section>

        <Section title="労働条件統計">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b border-gray-200">
                <th className="py-2">氏名</th><th>勤続</th><th>年齢</th><th>雇用形態</th><th>賃金形態</th>
                <th className="text-right">月平均所定</th><th className="text-right">所定/日</th>
              </tr>
            </thead>
            <tbody>
              {data.conditions.map((c) => (
                <tr key={c.name} className="border-b border-gray-50">
                  <td className="py-2 font-medium text-gray-700">{c.name}</td>
                  <td className="text-gray-500">{c.tenure}</td>
                  <td className="text-gray-500">{c.age ?? '-'}歳</td>
                  <td>{c.type}</td><td className="text-gray-500">{c.wageType}</td>
                  <td className="text-right">{fmtH(c.monthlyScheduled)}h</td>
                  <td className="text-right text-gray-500">{fmtH(c.dailyScheduled)}h</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>

        <div className="grid md:grid-cols-3 gap-6">
          <Section title="賃金総額ランキング" note="基本給＋手当（月額）">
            <ol className="space-y-2">
              {data.wageRank.map((w) => {
                const rank = data.wageRank.findIndex((x) => Math.round(x.total) === Math.round(w.total)) + 1;
                return (
                <li key={w.name} className="flex items-center gap-3 text-sm">
                  <span className={`w-6 text-center font-bold ${rank <= 3 ? 'text-pink-dark' : 'text-gray-400'}`}>{rank}</span>
                  <span className="font-medium text-gray-700">{w.name}</span>
                  <span className="ml-auto font-bold text-gray-800">{fmtY(w.total)}円</span>
                </li>
                );
              })}
            </ol>
          </Section>
          <Section title="時給ランキング" note="基本給＋基準内手当の時給（時給者は基本給（時給）欄の値）">
            <ol className="space-y-2">
              {data.wageHourly.map((w) => {
                const rank = data.wageHourly.findIndex((x) => Math.round(x.hourly) === Math.round(w.hourly)) + 1;
                return (
                <li key={w.name} className="flex items-center gap-3 text-sm">
                  <span className={`w-6 text-center font-bold ${rank <= 3 ? 'text-emerald-600' : 'text-gray-400'}`}>{rank}</span>
                  <span className="font-medium text-gray-700">{w.name}</span>
                  <span className="ml-auto font-bold text-gray-800">{fmtY(w.hourly)}円/h</span>
                </li>
                );
              })}
            </ol>
          </Section>
          <Section title="時間外労働 単価ランキング" note="基本給＋基準内手当の時給×割増率">
            <ol className="space-y-2">
              {data.otRate.map((o) => {
                const rank = data.otRate.findIndex((x) => Math.round(x.unit) === Math.round(o.unit)) + 1;
                return (
                <li key={o.name} className="flex items-center gap-3 text-sm">
                  <span className={`w-6 text-center font-bold ${rank <= 3 ? 'text-amber-600' : 'text-gray-400'}`}>{rank}</span>
                  <span className="font-medium text-gray-700">{o.name}</span>
                  <span className="text-xs text-gray-400">割増{o.rate}%</span>
                  <span className="ml-auto font-bold text-gray-800">{fmtY(o.unit)}円/h</span>
                </li>
                );
              })}
            </ol>
          </Section>
        </div>
      </div>
    </div>
  );
};

// 年度開始月は4月固定（＝3月決算）。
const FY_START_MONTH = 4;
const defaultFyOf = (startMonth) => { const d = new Date(); return (d.getMonth() + 1) >= startMonth ? d.getFullYear() : d.getFullYear() - 1; };

// ====================== 月次売上（顧客ごと・モーダル） ======================
const SalesPanel = ({ customer, viewAs, onClose, onSaved }) => {
  const [monthly, setMonthly] = useState({});
  const [rates, setRates] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const startMonth = FY_START_MONTH;
  const [fy, setFy] = useState(defaultFyOf(startMonth));
  const vq = viewAs ? `&view_as=${encodeURIComponent(viewAs)}` : '';

  useEffect(() => {
    setLoading(true);
    adminFetch(`/api/admin-report.php?action=sales&customer=${encodeURIComponent(customer.id)}&t=${Date.now()}${vq}`)
      .then((r) => r.json()).then((d) => {
        setMonthly(d && d.monthly && typeof d.monthly === 'object' ? d.monthly : {});
        setRates(d && d.rates && typeof d.rates === 'object' ? d.rates : {});
      })
      .catch(() => { setMonthly({}); setRates({}); }).finally(() => setLoading(false));
  }, [customer.id, vq]);

  // 年度 fy の12か月（開始月＝4月）
  const months = [];
  for (let i = 0; i < 12; i++) {
    const m0 = startMonth + i;
    const y = m0 <= 12 ? fy : fy + 1;
    const mm = ((m0 - 1) % 12) + 1;
    months.push({ key: `${y}-${String(mm).padStart(2, '0')}`, label: `${y}年${mm}月` });
  }
  const setVal = (key, v) => setMonthly((prev) => ({ ...prev, [key]: v }));
  const total = months.reduce((s, m) => s + (Number(monthly[m.key]) || 0), 0);

  // 年度ごとの粗利率・労働分配率（未設定時は旧 customer 値を初期値に）
  const fyKey = String(fy);
  const defRate = { grossMargin: customer.grossMargin ?? 85, laborShare: customer.laborShare ?? 50, stdHours: 163, plannedProd: 5000, laborBudget: 0 };
  const fr = rates[fyKey] || defRate;
  const setRate = (field, v) => setRates((prev) => ({ ...prev, [fyKey]: { ...(prev[fyKey] || defRate), [field]: v } }));

  const save = async () => {
    setSaving(true); setMsg('');
    try {
      const res = await adminFetch('/api/admin-report.php', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'save_sales', customerId: customer.id, monthly, rates: { ...rates, [fyKey]: fr }, ...(viewAs ? { view_as: viewAs } : {}) }),
      });
      const d = await res.json();
      setMsg(d.success ? '保存しました' : (d.message || '保存に失敗しました'));
      if (d.success) { onSaved && onSaved(); setTimeout(() => setMsg(''), 1500); }
    } catch { setMsg('保存に失敗しました'); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center p-4 overflow-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center px-6 py-4 border-b border-gray-100">
          <h3 className="text-lg font-bold">月次売上：<span className="text-indigo-600">{customer.name}</span></h3>
          {msg && <span className="ml-3 text-sm text-green-600">{msg}</span>}
          <button onClick={onClose} className="ml-auto text-gray-400 hover:text-gray-600 text-2xl leading-none">×</button>
        </div>
        <div className="px-6 py-4">
          <div className="flex items-center gap-3 mb-4">
            <label className="text-sm font-medium text-gray-700">年度</label>
            <button onClick={() => setFy(fy - 1)} className="px-2 py-1 bg-gray-100 rounded hover:bg-gray-200 text-sm">◀</button>
            <span className="font-bold text-gray-800 w-24 text-center">{fy}年度</span>
            <button onClick={() => setFy(fy + 1)} className="px-2 py-1 bg-gray-100 rounded hover:bg-gray-200 text-sm">▶</button>
            <span className="ml-auto text-sm text-gray-500">年間合計 <span className="font-bold text-gray-800">{fmtY(total)}</span> 円</span>
          </div>
          <div className="flex flex-wrap items-end gap-4 mb-5 bg-gray-50 rounded-lg px-4 py-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">粗利率（%）</label>
              <input type="number" value={fr.grossMargin ?? ''} onChange={(e) => setRate('grossMargin', e.target.value === '' ? '' : Number(e.target.value))}
                className="w-24 text-right border border-gray-200 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-400" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">労働分配率（%）</label>
              <input type="number" value={fr.laborShare ?? ''} onChange={(e) => setRate('laborShare', e.target.value === '' ? '' : Number(e.target.value))}
                className="w-24 text-right border border-gray-200 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-400" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">標準労働時間/月</label>
              <input type="number" value={fr.stdHours ?? ''} onChange={(e) => setRate('stdHours', e.target.value === '' ? '' : Number(e.target.value))}
                className="w-24 text-right border border-gray-200 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-400" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">予定人時生産性（円）</label>
              <input type="number" value={fr.plannedProd ?? ''} onChange={(e) => setRate('plannedProd', e.target.value === '' ? '' : Number(e.target.value))}
                className="w-28 text-right border border-gray-200 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-400" />
            </div>
            <span className="text-[11px] text-gray-400">※ {fy}年度に適用される基本データ（人時生産性分析で使用）</span>
          </div>
          {loading ? <div className="text-gray-500 py-8 text-center">読み込み中…</div> : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {months.map((m) => (
                <div key={m.key}>
                  <label className="block text-xs text-gray-500 mb-1">{m.label}</label>
                  <input type="number" value={monthly[m.key] ?? ''} onChange={(e) => setVal(m.key, e.target.value)} placeholder="0"
                    className="w-full text-right border border-gray-200 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-400" />
                </div>
              ))}
            </div>
          )}
          <p className="text-[11px] text-gray-400 mt-3">年度は{startMonth}月開始。金額は円単位で入力してください。</p>
        </div>
        <div className="flex items-center gap-2 px-6 py-4 border-t border-gray-100">
          <button onClick={save} disabled={saving || loading}
            className="bg-indigo-600 text-white px-5 py-2 rounded-lg text-sm font-bold hover:bg-indigo-700 disabled:opacity-50">
            {saving ? '保存中…' : '売上を保存'}
          </button>
          <button onClick={onClose} className="bg-gray-100 text-gray-600 px-4 py-2 rounded-lg text-sm font-bold hover:bg-gray-200">閉じる</button>
        </div>
      </div>
    </div>
  );
};

// ====================== 社員の職種設定（顧客ごと・モーダル） ======================
const EmployeePanel = ({ customer, jobs, viewAs, onClose }) => {
  const [emps, setEmps] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const vq = viewAs ? `&view_as=${encodeURIComponent(viewAs)}` : '';

  useEffect(() => {
    setLoading(true);
    adminFetch(`/api/admin-report.php?action=employees&customer=${encodeURIComponent(customer.id)}&t=${Date.now()}${vq}`)
      .then((r) => r.json()).then((d) => setEmps(Array.isArray(d) ? d : []))
      .catch(() => setEmps([])).finally(() => setLoading(false));
  }, [customer.id, vq]);

  const setJob = (key, name) => {
    const job = jobs.find((j) => j.name === name);
    setEmps((prev) => prev.map((e) => (e.key === key ? { ...e, jobName: name, jobCode: job ? job.code : null } : e)));
  };
  const setJobClass = (key, name) => {
    const job = jobs.find((j) => j.name === name);
    setEmps((prev) => prev.map((e) => (e.key === key ? { ...e, jobClass: name, jobClassCode: job ? job.code : null } : e)));
  };
  // CSVの職種名を派遣賃金マスタへ振り分け（①完全一致 ②含む中で最も粗い中分類 ③無ければ生名）
  const matchJob = (csvName) => {
    const nm = String(csvName || '').trim();
    if (!nm) return null;
    const ex = jobs.find((x) => x.name === nm);
    if (ex) return ex;
    const cl = (x) => { const c = String(x.code || ''); return c === '' ? 99 : c.length; };
    let best = null;
    jobs.forEach((x) => { if (x.name && x.name.includes(nm)) { if (!best || cl(x) < cl(best)) best = x; } });
    return best;
  };
  const setExcluded = (key, val) => setEmps((prev) => prev.map((e) => (e.key === key ? { ...e, excluded: val } : e)));
  // CSVの職種名(S列)から職種・職種区分へ一括振り分け（上書き）
  const applyCsvJob = () => setEmps((prev) => prev.map((e) => {
    if (!e.csvJobName) return e;
    const j = matchJob(e.csvJobName);
    // マスタに一致したものだけ設定。一致しないもの（マスタに無い職種名）は変更しない＝手選択。
    if (j) return { ...e, jobName: j.name, jobCode: j.code || null, jobClass: j.name, jobClassCode: j.code || null };
    return e;
  }));

  const save = async () => {
    setSaving(true); setMsg('');
    try {
      const res = await adminFetch('/api/admin-report.php', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'save_employees', customerId: customer.id, employees: emps, ...(viewAs ? { view_as: viewAs } : {}) }),
      });
      const d = await res.json();
      setMsg(d.success ? '保存しました' : (d.message || '保存に失敗しました'));
      if (d.success) setTimeout(() => setMsg(''), 1500);
    } catch { setMsg('保存に失敗しました'); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center p-4 overflow-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-5xl my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center px-6 py-4 border-b border-gray-100">
          <h3 className="text-lg font-bold">社員の職種設定：<span className="text-indigo-600">{customer.name}</span></h3>
          {msg && <span className="ml-3 text-sm text-green-600">{msg}</span>}
          <button onClick={onClose} className="ml-auto text-gray-400 hover:text-gray-600 text-2xl leading-none">×</button>
        </div>
        <div className="px-6 py-4">
          {loading && <div className="text-gray-500 py-8 text-center">読み込み中…</div>}
          {!loading && emps && emps.length === 0 && (
            <div className="text-gray-400 py-8 text-center">この顧客の取込データがありません。「CSV取り込み」で勤怠データを保存すると、社員が一覧に出ます。</div>
          )}
          {!loading && emps && emps.length > 0 && (
            <>
              <div className="flex items-center flex-wrap gap-2 mb-3">
                <p className="text-xs text-gray-500">取込データ（月次）から作成した社員名簿です。職種区分は適正賃金の基準に使用します（再取込しても保持されます）。</p>
                <button onClick={applyCsvJob} title="CSVの職種名を派遣賃金マスタの職種に自動で振り分けます" className="ml-auto bg-indigo-50 text-indigo-700 px-3 py-1.5 rounded-lg text-xs font-bold hover:bg-indigo-100">CSVから自動振り分け</button>
              </div>
              <datalist id="haken-jobs">
                {jobs.map((j) => <option key={(j.code || '') + j.name} value={j.name}>{j.code ? `${j.code}　${j.name}` : j.name}</option>)}
              </datalist>
              <div className="overflow-auto max-h-[60vh] border border-gray-100 rounded-lg">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-gray-500 sticky top-0">
                    <tr className="text-left">
                      <th className="px-3 py-2">社員番号</th><th className="px-3 py-2">氏名</th>
                      <th className="px-3 py-2">雇用形態</th>
                      <th className="px-3 py-2">職種コード</th><th className="px-3 py-2">職種</th>
                      <th className="px-3 py-2">職種区分（適正賃金の基準）</th>
                      <th className="px-3 py-2">レポート計算</th>
                    </tr>
                  </thead>
                  <tbody>
                    {emps.map((e) => (
                      <tr key={e.key} className="border-t border-gray-50">
                        <td className="px-3 py-2 text-gray-500">{e.empNo || '—'}</td>
                        <td className="px-3 py-2 font-medium text-gray-700">{e.name || '—'}</td>
                        <td className="px-3 py-2 text-gray-500">{e.employmentType || '—'}</td>
                        <td className="px-3 py-2 text-gray-500 tabular-nums">{e.jobCode || '—'}</td>
                        <td className="px-3 py-2">
                          <input list="haken-jobs" value={e.jobName || ''} onChange={(ev) => setJob(e.key, ev.target.value)}
                            placeholder="検索・選択" title="入力で絞り込み、一覧から選択"
                            className="w-56 border border-gray-200 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-400" />
                        </td>
                        <td className="px-3 py-2">
                          <input list="haken-jobs" value={e.jobClass || ''} onChange={(ev) => setJobClass(e.key, ev.target.value)}
                            placeholder="検索・選択" title="入力で絞り込み、一覧から選択"
                            className="w-56 border border-indigo-200 bg-indigo-50/40 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-400" />
                        </td>
                        <td className="px-3 py-2">
                          <button onClick={() => setExcluded(e.key, !e.excluded)}
                            title={e.excluded ? 'レポート計算から除外中（クリックで含める）' : 'レポート計算に含める（クリックで除外）'}
                            className={`px-3 py-1 rounded text-[11px] font-bold ${e.excluded ? 'bg-rose-100 text-rose-700 hover:bg-rose-200' : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'}`}>
                            {e.excluded ? '除外中' : '含める'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
        <div className="flex items-center gap-2 px-6 py-4 border-t border-gray-100">
          <button onClick={save} disabled={saving || loading || !emps || emps.length === 0}
            className="bg-indigo-600 text-white px-5 py-2 rounded-lg text-sm font-bold hover:bg-indigo-700 disabled:opacity-50">
            {saving ? '保存中…' : '職種を保存'}
          </button>
          <button onClick={onClose} className="bg-gray-100 text-gray-600 px-4 py-2 rounded-lg text-sm font-bold hover:bg-gray-200">閉じる</button>
        </div>
      </div>
    </div>
  );
};

// ====================== 顧客管理 ======================
const CustomerManager = ({ customers, reload, isPartner, myEmail, viewAs }) => {
  const [list, setList] = useState(customers);
  const [msg, setMsg] = useState('');
  const [saving, setSaving] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [regions, setRegions] = useState([]);
  const [empCustomer, setEmpCustomer] = useState(null);
  const [salesCustomer, setSalesCustomer] = useState(null);
  useEffect(() => { setList(customers); }, [customers]);
  useEffect(() => {
    adminFetch(`/api/admin-report.php?action=wage_options&t=${Date.now()}`)
      .then((r) => r.json())
      .then((d) => { setJobs(Array.isArray(d.jobs) ? d.jobs : []); setRegions(Array.isArray(d.regions) ? d.regions : []); })
      .catch(() => {});
  }, []);

  const save = async (next) => {
    setSaving(true);
    try {
      const res = await adminFetch('/api/admin-report.php', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'save_customers', customers: next, ...(viewAs ? { view_as: viewAs } : {}) }),
      });
      const d = await res.json();
      if (d.success) { setList(next); setMsg('保存しました'); setTimeout(() => setMsg(''), 1500); reload && reload(); }
      else setMsg('保存に失敗しました');
    } catch { setMsg('保存に失敗しました'); }
    setSaving(false);
  };

  const addCustomer = () => {
    const id = 'c' + Date.now().toString(36) + Math.floor(Math.random() * 1000);
    save([...list, { id, name: '新規顧客', sales: 0, grossMargin: 85, laborShare: 50, owner: isPartner ? myEmail : '' }]);
  };
  const update = (id, field, value) => setList(list.map((c) => (c.id === id ? { ...c, [field]: value } : c)));
  const commit = () => save(list);
  const remove = (id) => { if (window.confirm('この顧客を削除しますか？（取込データは残ります）')) save(list.filter((c) => c.id !== id)); };

  return (
    <div>
      <div className="flex items-center mb-6">
        <h2 className="text-2xl font-bold">顧客管理</h2>
        {msg && <span className="ml-4 text-sm text-green-600">{msg}</span>}
        <button onClick={addCustomer} disabled={saving} className="ml-auto bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-indigo-700 disabled:opacity-50">＋ 顧客を追加</button>
      </div>
      <p className="text-xs text-gray-500 mb-4">
        顧問先企業を登録します。売上などのデータは人時生産性分析で使用します。
        {isPartner ? '　表示されるのはあなたの担当顧客のみです。' : '　全顧客が表示されています。'}
      </p>

      <div className="bg-white rounded-xl shadow overflow-x-auto">
        <table className="w-full text-sm min-w-[860px]">
          <thead className="bg-gray-50 text-gray-500">
            <tr className="text-left">
              <th className="px-4 py-3">会社名</th>
              <th className="px-4 py-3">地域</th>
              <th className="px-4 py-3 text-right">社保加入基準時間</th>
              <th className="px-4 py-3 text-right">売上（当年度）</th>
              <th className="px-4 py-3 text-right">操作</th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-400">顧客が登録されていません。「＋ 顧客を追加」から登録してください。</td></tr>}
            {list.map((c) => (
              <tr key={c.id} className="border-t border-gray-100">
                <td className="px-4 py-2">
                  <input value={c.name} onChange={(e) => update(c.id, 'name', e.target.value)} onBlur={commit}
                    className="w-full border border-gray-200 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-indigo-400" />
                </td>
                <td className="px-4 py-2">
                  <select value={c.region || ''} onChange={(e) => update(c.id, 'region', e.target.value)} onBlur={commit}
                    className="w-28 border border-gray-200 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-400">
                    <option value="">未設定</option>
                    {regions.map((r) => <option key={r} value={r}>{r}</option>)}
                    {c.region && !regions.includes(c.region) && <option value={c.region}>{c.region}</option>}
                  </select>
                </td>
                <td className="px-4 py-2 text-right">
                  <input type="number" value={c.enrollmentHours ?? c.enrollmentDays ?? ''} onChange={(e) => update(c.id, 'enrollmentHours', e.target.value === '' ? '' : Number(e.target.value))} onBlur={commit}
                    className="w-20 text-right border border-gray-200 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-400" placeholder="時間/週" title="社会保険の加入基準となる週あたりの労働時間。個人別労働時間グラフの基準線に使用" />
                </td>
                <td className="px-4 py-2 text-right tabular-nums whitespace-nowrap text-gray-700">
                  {c.salesFyTotal ? `${fmtY(c.salesFyTotal)} 円` : <span className="text-gray-400">未入力</span>}
                </td>
                <td className="px-4 py-2 text-right whitespace-nowrap">
                  <button onClick={() => setSalesCustomer(c)} className="bg-emerald-50 text-emerald-700 px-3 py-1 rounded text-[11px] font-bold hover:bg-emerald-100 mr-1">売上</button>
                  <button onClick={() => setEmpCustomer(c)} className="bg-indigo-50 text-indigo-600 px-3 py-1 rounded text-[11px] font-bold hover:bg-indigo-100 mr-1">社員・職種</button>
                  <button onClick={() => remove(c.id)} className="bg-red-50 text-red-600 px-3 py-1 rounded text-[11px] font-bold hover:bg-red-100">削除</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {empCustomer && <EmployeePanel customer={empCustomer} jobs={jobs} viewAs={viewAs} onClose={() => setEmpCustomer(null)} />}
      {salesCustomer && <SalesPanel customer={salesCustomer} viewAs={viewAs} onClose={() => setSalesCustomer(null)} onSaved={reload} />}
    </div>
  );
};

// ====================== CSV取り込み ======================
const CsvImport = ({ customers, viewAs, reload, customerId: globalCustomerId = '' }) => {
  const [customerId, setCustomerId] = useState(globalCustomerId || '');
  // サイドバーの共通顧客選択に追従（画面内でも取込先は変更可能）
  useEffect(() => { if (globalCustomerId) setCustomerId(globalCustomerId); }, [globalCustomerId]);
  const [parsed, setParsed] = useState(null);
  const [fileName, setFileName] = useState('');
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');
  const [saving, setSaving] = useState(false);
  const [stored, setStored] = useState(null);
  const inputRef = useRef(null);
  const vq = viewAs ? `&view_as=${encodeURIComponent(viewAs)}` : '';

  const loadStored = useCallback((id) => {
    if (!id) { setStored(null); return; }
    adminFetch(`/api/admin-report.php?action=import_data&customer=${encodeURIComponent(id)}&t=${Date.now()}${vq}`)
      .then((r) => r.json()).then((d) => setStored(d)).catch(() => setStored(null));
  }, [vq]);
  useEffect(() => { loadStored(customerId); }, [customerId, loadStored]);

  const handleFile = (file) => {
    if (!file) return;
    setError(''); setMsg('');
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const rows = parseCSV(e.target.result);
        if (!rows.length) { setError('データ行が見つかりませんでした。'); return; }
        if (!('年月' in rows[0])) { setError('「年月」列が見つかりません。勤怠出力CSVか確認してください。'); return; }
        setParsed(rows); setFileName(file.name);
      } catch (err) { setError('CSVの解析に失敗しました：' + err.message); }
    };
    reader.readAsText(file, 'UTF-8');
  };

  const sum = parsed ? summarizeRows(parsed) : null;
  // CSVの企業名（勤務地違いがあっても企業名は1つの想定。複数あれば手動選択を促す）
  const csvCompany = sum && sum.companies.length ? sum.companies[0] : '';
  const existingMatch = csvCompany ? customers.find((c) => (c.name || '').trim() === csvCompany.trim()) : null;

  const doSave = async () => {
    if (!parsed) return;
    // 顧客未選択時はCSVの企業名で自動登録する
    if (!customerId) {
      if (!csvCompany) { setError('CSVに企業名がありません。紐づける顧客を選択してください。'); return; }
      if (sum.companies.length > 1) { setError('CSVに複数の企業名があります。紐づける顧客を手動で選択してください。'); return; }
    }
    setSaving(true); setError(''); setMsg('');
    try {
      const body = customerId
        ? { action: 'save_import', customerId, rows: parsed, fileName }
        : { action: 'import_auto', companyName: csvCompany, rows: parsed, fileName };
      const res = await adminFetch('/api/admin-report.php', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...body, ...(viewAs ? { view_as: viewAs } : {}) }),
      });
      const d = await res.json();
      if (d.success) {
        const head = d.customerName ? (d.created ? `「${d.customerName}」を新規登録し、` : `「${d.customerName}」に`) : '';
        setMsg(`${head}保存しました（${d.rowCount}行 / ${d.months.length}か月分。同月は差し替え済み）`);
        setParsed(null); setFileName('');
        if (d.customerId) { setCustomerId(d.customerId); loadStored(d.customerId); } else loadStored(customerId);
        reload && reload();
      } else setError(d.message || '保存に失敗しました');
    } catch { setError('保存に失敗しました'); }
    setSaving(false);
  };

  return (
    <div>
      <h2 className="text-2xl font-bold mb-6">CSV取り込み</h2>

      <div className="bg-white rounded-xl shadow p-6 mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">紐づける顧客（任意）</label>
        <select value={customerId} onChange={(e) => setCustomerId(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 min-w-[16rem]">
          <option value="">— 自動（CSVの企業名で登録）—</option>
          {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <p className="text-xs text-gray-500 mt-2">未選択の場合、CSVの「企業名」で顧客管理に自動登録（同名があれば再利用）して取り込みます。</p>
        {stored && (stored.months?.length > 0) && (
          <p className="text-xs text-gray-500 mt-3">この顧客の蓄積データ：{stored.rows.length}行 / {stored.months.length}か月（{monthLabel(stored.months[0])}〜{monthLabel(stored.months[stored.months.length - 1])}）</p>
        )}
      </div>

      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); handleFile(e.dataTransfer.files[0]); }}
        className="bg-white rounded-xl shadow border-2 border-dashed border-gray-200 p-10 text-center mb-6"
      >
        <div className="text-4xl mb-3">📄</div>
        <p className="text-gray-600 mb-1 font-medium">勤怠出力CSVをドラッグ＆ドロップ</p>
        <p className="text-xs text-gray-400 mb-5">またはファイルを選択</p>
        <button onClick={() => inputRef.current?.click()} className="bg-indigo-600 text-white px-6 py-2.5 rounded-lg text-sm font-bold hover:bg-indigo-700">CSVファイルを選択</button>
        <input ref={inputRef} type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => handleFile(e.target.files[0])} />
        {error && <div className="mt-5 text-sm text-rose-600 bg-rose-50 rounded-lg px-4 py-2 inline-block">{error}</div>}
        {msg && <div className="mt-5 text-sm text-green-600 bg-green-50 rounded-lg px-4 py-2 inline-block">{msg}</div>}
      </div>

      {parsed && sum && (
        <div className="bg-white rounded-xl shadow p-6">
          <h3 className="font-bold text-lg mb-4">取込内容の確認</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5 text-sm">
            <div><div className="text-gray-400 text-xs">ファイル</div><div className="font-medium truncate">{fileName}</div></div>
            <div><div className="text-gray-400 text-xs">企業名</div><div className="font-medium">{sum.companies.join('、') || '—'}</div></div>
            <div><div className="text-gray-400 text-xs">対象月</div><div className="font-medium">{sum.months.length}か月（{monthLabel(sum.months[0])}〜{monthLabel(sum.months[sum.months.length - 1])}）</div></div>
            <div><div className="text-gray-400 text-xs">従業員</div><div className="font-medium">{sum.emps.length}名 / {parsed.length}行</div></div>
          </div>
          {!customerId && csvCompany && (
            <p className="text-xs text-indigo-600 bg-indigo-50 rounded-lg px-4 py-2 mb-3 inline-block">
              企業名「{csvCompany}」{existingMatch ? 'の既存顧客に追加' : 'を顧客管理に新規登録して追加'}します。
            </p>
          )}
          <div className="flex gap-2">
            <button onClick={doSave} disabled={saving || (!customerId && !csvCompany)} className="bg-indigo-600 text-white px-6 py-2.5 rounded-lg text-sm font-bold hover:bg-indigo-700 disabled:opacity-50">
              {saving ? '保存中…' : (customerId ? 'この顧客に保存（同月は差し替え）' : '自動登録して保存（同月は差し替え）')}
            </button>
            <button onClick={() => { setParsed(null); setFileName(''); }} className="bg-gray-100 text-gray-600 px-4 py-2.5 rounded-lg text-sm font-bold hover:bg-gray-200">取消</button>
          </div>
        </div>
      )}
    </div>
  );
};

// ====================== マンスリー集計（顧客選択→保存済みデータ表示） ======================
const MonthlyView = ({ customers, viewAs, customerId = '' }) => {
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const vq = viewAs ? `&view_as=${encodeURIComponent(viewAs)}` : '';

  useEffect(() => {
    if (!customerId) { setRows(null); return; }
    setLoading(true);
    adminFetch(`/api/admin-report.php?action=import_data&customer=${encodeURIComponent(customerId)}&t=${Date.now()}${vq}`)
      .then((r) => r.json())
      .then((d) => setRows(Array.isArray(d.rows) ? d.rows : []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [customerId, vq]);

  return (
    <div>
      <h2 className="text-2xl font-bold mb-6">マンスリー集計</h2>
      {!customerId && <div className="bg-white rounded-xl shadow p-8 text-center text-gray-400">左メニュー下部の「顧客」から選択してください。</div>}
      {loading && <div className="text-gray-500">読み込み中…</div>}
      {!loading && customerId && rows && rows.length === 0 && (
        <div className="bg-white rounded-xl shadow p-8 text-center text-gray-400">
          この顧客の取込データがありません。「CSV取り込み」からデータを保存してください。
        </div>
      )}
      {!loading && rows && rows.length > 0 && (() => {
        const sc = customers.find((c) => c.id === customerId);
        return <MonthlyReport rows={rows} enrollmentHours={sc ? (sc.enrollmentHours ?? sc.enrollmentDays) : undefined} />;
      })()}
    </div>
  );
};

// ====================== 人員構成分析（年齢構成の将来予測＋AI評価） ======================
const AGE_BRACKETS = ['20代前半', '20代後半', '30代前半', '30代後半', '40代前半', '40代後半', '50代前半', '50代後半', '60代前半', '60代後半', '70代前半', '70代後半'];
const OFFSETS = [{ o: 0, label: '今' }, { o: 3, label: '3年後' }, { o: 5, label: '5年後' }, { o: 7, label: '7年後' }, { o: 10, label: '10年後' }];
const birthYearOf = (s) => { const m = String(s || '').match(/(\d{4})/); return m ? parseInt(m[1], 10) : null; };
const bracketOf = (age) => {
  if (age == null || isNaN(age)) return null;
  const a = Math.max(20, Math.min(79, age)); // 20〜79に丸めて12区分に集約（エクセル準拠）
  return `${Math.floor(a / 10) * 10}代${(a % 10) < 5 ? '前半' : '後半'}`;
};
// 年代別在籍割合：1時点ぶんの棒グラフ（横軸=年代、縦軸=人数）をSVG文字列で返す。
// エクセル「人員構成分析シート」に合わせ、時点ごとに1グラフを並べる。
const ageBarSVG = ({ title, items }) => {
  const W = 250, padL = 20, padR = 8, padT = 20, chartH = 96, padB = 52;
  const n = Math.max(1, items.length);
  const max = Math.max(1, ...items.map((i) => i.count));
  const slot = (W - padL - padR) / n;
  const bw = Math.min(15, slot * 0.62);
  let g = `<text x="${(W / 2).toFixed(0)}" y="12" font-size="10" fill="#374151" text-anchor="middle">${title}</text>`;
  g += `<line x1="${padL}" y1="${padT}" x2="${padL}" y2="${padT + chartH}" stroke="#e5e7eb"/>`
    + `<line x1="${padL}" y1="${padT + chartH}" x2="${W - padR}" y2="${padT + chartH}" stroke="#e5e7eb"/>`
    + `<text x="${padL - 3}" y="${padT + chartH}" font-size="7" fill="#9ca3af" text-anchor="end">0</text>`
    + `<text x="${padL - 3}" y="${padT + 6}" font-size="7" fill="#9ca3af" text-anchor="end">${max}</text>`;
  items.forEach((it, i) => {
    const x = padL + slot * i + slot / 2;
    const h = it.count / max * chartH;
    const y = padT + chartH - h;
    if (it.count > 0) {
      g += `<rect x="${(x - bw / 2).toFixed(1)}" y="${y.toFixed(1)}" width="${bw.toFixed(1)}" height="${h.toFixed(1)}" fill="#4f79c4"/>`;
      g += `<text x="${x.toFixed(1)}" y="${(y - 2).toFixed(1)}" font-size="7" fill="#374151" text-anchor="middle">${it.count}</text>`;
    }
    g += `<text x="${x.toFixed(1)}" y="${(padT + chartH + 4).toFixed(1)}" font-size="6.5" fill="#6b7280" text-anchor="end" transform="rotate(-55 ${x.toFixed(1)} ${(padT + chartH + 4).toFixed(1)})">${it.label}</text>`;
  });
  const H = padT + chartH + padB;
  return `<svg viewBox="0 0 ${W} ${H}" width="100%" style="max-width:260px" xmlns="http://www.w3.org/2000/svg">${g}</svg>`;
};

const WorkforceAnalysis = ({ customers, viewAs, customerId = '' }) => {
  const [emps, setEmps] = useState(null);
  const [loading, setLoading] = useState(false);
  const [retire, setRetire] = useState(65);
  const [evalData, setEvalData] = useState(null);
  const [generatedAt, setGeneratedAt] = useState('');
  const [evaluating, setEvaluating] = useState(false);
  const [evalErr, setEvalErr] = useState('');
  const vq = viewAs ? `&view_as=${encodeURIComponent(viewAs)}` : '';
  const curYear = new Date().getFullYear();

  useEffect(() => {
    if (!customerId) { setEmps(null); setEvalData(null); setGeneratedAt(''); return; }
    setLoading(true); setEvalData(null); setGeneratedAt(''); setEvalErr('');
    adminFetch(`/api/admin-report.php?action=employees&customer=${encodeURIComponent(customerId)}&t=${Date.now()}${vq}`)
      .then((r) => r.json()).then((d) => {
        const arr = Array.isArray(d) ? d : [];
        setEmps(arr);
        // 定年はCSVの「定年の定め（…）」列の最大値（会社単位）を自動セット
        const cr = Math.max(0, ...arr.map((e) => Number(e.retireMax) || 0));
        if (cr > 0) setRetire(cr);
      })
      .catch(() => setEmps([])).finally(() => setLoading(false));
    // 保存済みAI評価があれば表示（API再呼び出しなし）
    adminFetch(`/api/admin-report.php?action=workforce_saved&customer=${encodeURIComponent(customerId)}&t=${Date.now()}${vq}`)
      .then((r) => r.json()).then((d) => { if (d && d.eval) { setEvalData(d.eval); setGeneratedAt(d.generatedAt || ''); } })
      .catch(() => {});
  }, [customerId, vq]);

  const years = useMemo(() => OFFSETS.map((x) => ({ ...x, year: curYear + x.o, label: `${x.label}（${curYear + x.o}）` })), [curYear]);
  const rows = useMemo(() => (emps || []).filter((e) => !e.excluded).map((e) => {
    const by = birthYearOf(e.birth);
    return { ...e, birthYear: by, ages: years.map((y) => (by != null ? y.year - by : null)) };
  }).sort((a, b) => {
    // エクセルに合わせて社員番号順（登録順）。数値なら数値比較、それ以外は文字比較。
    const na = parseInt(a.empNo, 10), nb = parseInt(b.empNo, 10);
    if (!isNaN(na) && !isNaN(nb) && na !== nb) return na - nb;
    return String(a.empNo || '').localeCompare(String(b.empNo || ''), 'ja');
  }), [emps, years]);
  const bracketCounts = useMemo(() => AGE_BRACKETS.map((label) => ({
    label, counts: years.map((y, yi) => rows.filter((r) => bracketOf(r.ages[yi]) === label).length),
  })), [rows, years]);
  // 現在の職種構成（採用提案の材料としてAIへ渡す）
  const jobCounts = useMemo(() => {
    const m = {};
    rows.forEach((r) => { const j = (r.jobName || '').trim() || '未設定'; m[j] = (m[j] || 0) + 1; });
    return Object.entries(m).map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count);
  }, [rows]);

  const withBirth = rows.filter((r) => r.birthYear != null);
  const company = customers.find((c) => c.id === customerId)?.name || '';

  const runEval = async () => {
    setEvaluating(true); setEvalErr(''); setEvalData(null);
    try {
      const res = await adminFetch('/api/admin-report.php', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'workforce_eval', customerId, companyName: company, retirementAge: Number(retire),
          years: years.map((y) => ({ label: y.label })),
          brackets: bracketCounts.filter((b) => b.counts.some((c) => c > 0)),
          jobs: jobCounts,
          ...(viewAs ? { view_as: viewAs } : {}),
        }),
      });
      const d = await res.json();
      if (d.success) { setEvalData(d.eval); setGeneratedAt(d.generatedAt || ''); }
      else setEvalErr(d.message || 'AI分析に失敗しました');
    } catch { setEvalErr('AI分析に失敗しました'); }
    setEvaluating(false);
  };

  const printReport = () => {
    const esc = (s) => String(s ?? '').replace(/[&<>]/g, (m) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[m]));
    const yTh = years.map((y) => `<th>${esc(y.label)}</th>`).join('');
    const brRows = bracketCounts.map((b) => `<tr><td>${esc(b.label)}</td>${b.counts.map((c) => `<td class="num${c > 0 ? '' : ' z'}">${c}</td>`).join('')}</tr>`).join('');
    const totRow = `<tr class="tot"><td>合計</td>${years.map(() => `<td class="num">${withBirth.length}</td>`).join('')}</tr>`;
    const empRows = rows.map((r) => `<tr><td>${esc(r.name || '—')}</td><td>${esc(r.jobName || '—')}</td>${r.ages.map((a) => `<td class="num${a != null && a >= Number(retire) ? ' over' : ''}">${a != null ? a + '歳' : '—'}</td>`).join('')}</tr>`).join('');
    let evalHtml = '';
    if (evalData) {
      const good = (evalData.overall?.good || []).map((t) => `<li>${esc(t)}</li>`).join('');
      const risks = (evalData.overall?.risks || []).map((t) => `<li>${esc(t)}</li>`).join('');
      const gens = (evalData.byGeneration || []).map((g) => `<div class="gen"><div class="genlabel">${esc(g.label)}</div><div><b>良い点：</b>${esc(g.good)}</div><div><b>注意：</b>${esc(g.risk)}</div></div>`).join('');
      let hiringHtml = '';
      if (evalData.hiring) {
        const hsum = evalData.hiring.summary ? `<p class="hsum">${esc(evalData.hiring.summary)}</p>` : '';
        const profs = (evalData.hiring.profiles || []).map((p) => `<div class="prof"><div class="proft">${esc(p.title)}</div><div>${esc(p.detail)}</div><div class="profr"><b>狙い：</b>${esc(p.reason)}</div></div>`).join('');
        hiringHtml = `<h3>今、採用するなら（AI提案）</h3>${hsum}${profs}`;
      }
      evalHtml = `<h2>AIによる評価${generatedAt ? ` <span class="meta">（生成：${esc(new Date(generatedAt).toLocaleString('ja-JP'))}）</span>` : ''}</h2>`
        + `<div class="cols"><div class="box good"><h3>良い点</h3><ul>${good}</ul></div><div class="box risk"><h3>注意すべき点・リスク</h3><ul>${risks}</ul></div></div>`
        + (gens ? `<h3>年代別評価</h3>${gens}` : '')
        + hiringHtml;
    }
    const html = `<!doctype html><html lang="ja"><head><meta charset="utf-8"><title>組織人員構成分析レポート</title>`
      + `<style>`
      + `body{font-family:"Hiragino Kaku Gothic ProN","Yu Gothic",sans-serif;color:#222;margin:24px;}`
      + `h1{font-size:20px;margin:0 0 4px;}.sub{color:#666;font-size:12px;margin-bottom:16px;}`
      + `h2{font-size:15px;border-bottom:2px solid #ddd;padding-bottom:4px;margin:22px 0 10px;}h3{font-size:13px;margin:12px 0 6px;}`
      + `table{border-collapse:collapse;width:100%;font-size:12px;margin-bottom:8px;}th,td{border:1px solid #ddd;padding:5px 8px;text-align:left;}th{background:#f3f3f3;}`
      + `td.num{text-align:right;}td.z{color:#bbb;}td.over{color:#d6336c;font-weight:bold;}tr.tot td{font-weight:bold;background:#fafafa;}`
      + `.cols{display:flex;gap:16px;}.box{flex:1;border-radius:8px;padding:10px 14px;}.box.good{background:#ecfdf3;}.box.risk{background:#fef2f4;}`
      + `.box ul{margin:6px 0 0;padding-left:18px;}.box li{margin:4px 0;font-size:12px;}`
      + `.gen{border:1px solid #eee;border-radius:6px;padding:8px 10px;margin:6px 0;font-size:12px;}.genlabel{font-weight:bold;color:#4f46e5;margin-bottom:3px;}.meta{color:#999;font-weight:normal;font-size:11px;}`
      + `.hsum{background:#eef2ff;border-radius:6px;padding:8px 10px;font-size:12px;margin:6px 0;}.prof{border:1px solid #e0e7ff;border-radius:6px;padding:8px 10px;margin:6px 0;font-size:12px;}.proft{font-weight:bold;color:#4f46e5;margin-bottom:3px;}.profr{color:#666;margin-top:3px;}`
      + `@page{margin:0;}@media print{body{margin:12mm;}*{-webkit-print-color-adjust:exact;print-color-adjust:exact;}}`
      + `</style></head><body>`
      + `<h1>組織人員構成分析レポート</h1>`
      + `<div class="sub">${esc(company || '')}　／　定年 ${esc(retire)}歳　／　出力 ${esc(new Date().toLocaleDateString('ja-JP'))}</div>`
      + `<h2>社員別 年齢推移</h2><table><thead><tr><th>氏名</th><th>職種</th>${yTh}</tr></thead><tbody>${empRows}</tbody></table>`
      + `<h2>年代別在籍割合</h2><div style="display:flex;flex-wrap:wrap;gap:6px">${years.map((y, ti) => `<div style="width:18%">${ageBarSVG({ title: y.label, items: bracketCounts.map((b) => ({ label: b.label, count: b.counts[ti] })) })}</div>`).join('')}</div>`
      + `<h2>年代別人数の推移</h2><table><thead><tr><th>年代</th>${yTh}</tr></thead><tbody>${brRows}${totRow}</tbody></table>`
      + evalHtml
      + `</body></html>`;
    printHtml(html);
  };

  return (
    <div>
      <h2 className="text-2xl font-bold mb-1">組織人員構成分析</h2>
      <p className="text-xs text-gray-500 mb-6">取込データの生年月日から、現在および将来（3・5・7・10年後）の年齢構成を予測します。AI評価で良い点・注意点を自動生成できます。</p>

      {!customerId && <div className="bg-white rounded-xl shadow p-8 text-center text-gray-400 mb-6">左メニュー下部の「顧客」から選択してください。</div>}
      {customerId && <div className="bg-white rounded-xl shadow p-5 mb-6 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700">定年</label>
          <span className="text-lg font-bold text-gray-800">{retire}</span>
          <span className="text-sm text-gray-500">歳</span>
          <span className="text-[11px] text-gray-400">（CSVの定年の定めから自動取得）</span>
        </div>
        <button onClick={printReport} disabled={withBirth.length === 0}
          className="ml-auto bg-gray-800 text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-gray-900 disabled:opacity-40">PDF / 印刷</button>
      </div>}

      {loading && <div className="text-gray-500">読み込み中…</div>}
      {!loading && customerId && rows.length === 0 && (
        <div className="bg-white rounded-xl shadow p-8 text-center text-gray-400">この顧客の取込データがありません。「CSV取り込み」から保存してください。</div>
      )}
      {!loading && rows.length > 0 && withBirth.length === 0 && (
        <div className="bg-white rounded-xl shadow p-8 text-center text-rose-500">取込データに「生年月日」がありません。年齢構成を計算できません。</div>
      )}

      {!loading && withBirth.length > 0 && (
        <>
          {/* 社員別 年齢推移（エクセル同様、最上段） */}
          <section className="bg-white rounded-xl shadow p-6 mb-6">
            <h3 className="font-bold text-gray-800 mb-4">社員別 年齢推移<span className="text-xs text-gray-400 ml-2">定年{retire}歳以上は赤字</span></h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50"><tr className="text-left text-gray-500">
                  <th className="py-2 px-3">氏名</th><th className="py-2 px-3">職種</th>
                  {years.map((y) => <th key={y.o} className="py-2 px-3 text-right whitespace-nowrap">{y.label}</th>)}
                </tr></thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.key} className="border-b border-gray-50">
                      <td className="py-1.5 px-3 font-medium text-gray-700">{r.name || '—'}</td>
                      <td className="py-1.5 px-3 text-gray-500">{r.jobName || '—'}</td>
                      {r.ages.map((a, i) => (
                        <td key={i} className={`py-1.5 px-3 text-right tabular-nums ${a != null && a >= Number(retire) ? 'text-rose-600 font-bold' : 'text-gray-700'}`}>{a != null ? `${a}歳` : '—'}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* 年代別人数の推移 */}
          <section className="bg-white rounded-xl shadow p-6 mb-6">
            <h3 className="font-bold text-gray-800 mb-4">年代別人数の推移{company && <span className="text-sm text-gray-500 ml-2">{company}</span>}</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-5">
              {years.map((y, ti) => (
                <div key={y.o} className={`border border-gray-100 rounded-lg p-1 bg-white relative cursor-zoom-in transition-transform duration-150 hover:scale-[1.8] hover:z-30 hover:shadow-2xl ${ti === 0 ? 'origin-top-left' : ti === years.length - 1 ? 'origin-top-right' : 'origin-top'}`} title="カーソルを乗せると拡大">
                  <div dangerouslySetInnerHTML={{ __html: ageBarSVG({ title: y.label, items: bracketCounts.map((b) => ({ label: b.label, count: b.counts[ti] })) }) }} />
                </div>
              ))}
            </div>
            <div className="overflow-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-gray-500 border-b border-gray-200">
                  <th className="py-2 pr-4">年代</th>
                  {years.map((y) => <th key={y.o} className="py-2 px-3 text-right whitespace-nowrap">{y.label}</th>)}
                </tr></thead>
                <tbody>
                  {bracketCounts.map((b) => (
                    <tr key={b.label} className="border-b border-gray-50">
                      <td className="py-1.5 pr-4 text-gray-700">{b.label}</td>
                      {b.counts.map((c, i) => <td key={i} className={`py-1.5 px-3 text-right tabular-nums ${c > 0 ? 'text-gray-800 font-medium' : 'text-gray-300'}`}>{c}</td>)}
                    </tr>
                  ))}
                  <tr className="border-t-2 border-gray-200 font-bold">
                    <td className="py-2 pr-4">合計</td>
                    {years.map((y, yi) => <td key={yi} className="py-2 px-3 text-right tabular-nums">{withBirth.length}</td>)}
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* AI評価 */}
          <section className="bg-white rounded-xl shadow p-6">
            <div className="flex items-center flex-wrap gap-2 mb-4">
              <h3 className="font-bold text-gray-800">AIによる全体評価・年代別評価・採用提案</h3>
              {generatedAt && <span className="text-[11px] text-gray-400">生成日時：{new Date(generatedAt).toLocaleString('ja-JP')}（保存済み）</span>}
              <button onClick={runEval} disabled={evaluating}
                className="ml-auto bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-indigo-700 disabled:opacity-50">
                {evaluating ? 'AI分析中…（数十秒）' : evalData ? '再分析' : 'AIで評価を生成'}
              </button>
            </div>
            {evalErr && <div className="text-sm text-rose-600 bg-rose-50 rounded-lg px-4 py-2 mb-4">{evalErr}</div>}
            {!evalData && !evaluating && !evalErr && <p className="text-sm text-gray-400">「AIで評価を生成」を押すと、上の構成からAIが良い点・注意点を分析します。</p>}
            {evalData && (
              <div className="space-y-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-emerald-50 rounded-xl p-4">
                    <div className="font-bold text-emerald-700 mb-2">良い点</div>
                    <ul className="space-y-1.5 text-sm text-gray-700 list-disc pl-5">
                      {(evalData.overall?.good || []).map((t, i) => <li key={i}>{t}</li>)}
                    </ul>
                  </div>
                  <div className="bg-rose-50 rounded-xl p-4">
                    <div className="font-bold text-rose-700 mb-2">注意すべき点・リスク</div>
                    <ul className="space-y-1.5 text-sm text-gray-700 list-disc pl-5">
                      {(evalData.overall?.risks || []).map((t, i) => <li key={i}>{t}</li>)}
                    </ul>
                  </div>
                </div>
                {Array.isArray(evalData.byGeneration) && evalData.byGeneration.length > 0 && (
                  <div>
                    <div className="font-bold text-gray-700 mb-2">年代別評価</div>
                    <div className="space-y-2">
                      {evalData.byGeneration.map((g, i) => (
                        <div key={i} className="border border-gray-100 rounded-lg p-3">
                          <div className="font-bold text-indigo-600 text-sm mb-1">{g.label}</div>
                          <div className="text-sm text-gray-700"><span className="text-emerald-600 font-medium">良い点：</span>{g.good}</div>
                          <div className="text-sm text-gray-700"><span className="text-rose-600 font-medium">注意：</span>{g.risk}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {evalData.hiring && (
                  <div>
                    <div className="font-bold text-gray-700 mb-2">今、採用するなら（AI提案）</div>
                    {evalData.hiring.summary && <p className="text-sm text-gray-700 mb-3 bg-indigo-50 rounded-lg px-4 py-2">{evalData.hiring.summary}</p>}
                    <div className="grid md:grid-cols-2 gap-3">
                      {(evalData.hiring.profiles || []).map((p, i) => (
                        <div key={i} className="border border-indigo-100 rounded-lg p-3">
                          <div className="font-bold text-indigo-600 text-sm mb-1">{p.title}</div>
                          <div className="text-sm text-gray-700 mb-1">{p.detail}</div>
                          <div className="text-xs text-gray-500"><span className="font-medium text-gray-600">狙い：</span>{p.reason}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <p className="text-[11px] text-gray-400">※ AI（Claude）による自動生成です。最終判断はご自身で行ってください。</p>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
};

// ====================== 人時生産性分析／人件費予実管理（顧客管理の売上・率・勤怠と連動） ======================
const isShortType = (t) => /短時間|パート|アルバイト/.test(String(t || ''));
const fmtK = (n) => Math.round((Number(n) || 0) / 1000).toLocaleString('ja-JP'); // 円→千円表示

const LaborCostManager = ({ customers, viewAs, title = '人時生産性分析', profit = false, customerId = '' }) => {
  const [rows, setRows] = useState(null);
  const [pl, setPl] = useState({}); // { 'YYYY-MM': {bSales,bVar,bExp,aSales,aVar,aExp} } 円
  const [rates, setRates] = useState({}); // { 'YYYY': { avgSales(千円), varRate(%), laborRate(%) } } 予算の一括設定
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [fy, setFy] = useState(defaultFyOf(4));
  const [half, setHalf] = useState(0); // 0=前半6ヶ月 / 1=後半6ヶ月
  const [chartTab, setChartTab] = useState(0); // 0=売上 1=粗利 2=売上・粗利（各＋人時生産性）
  const chartRef = useRef(null);
  const vq = viewAs ? `&view_as=${encodeURIComponent(viewAs)}` : '';
  const startMonth = FY_START_MONTH;
  useEffect(() => { if (customerId) setFy(defaultFyOf(startMonth)); }, [customerId, startMonth]);

  useEffect(() => {
    if (!customerId) { setRows(null); setPl({}); return; }
    setLoading(true); setMsg('');
    Promise.all([
      adminFetch(`/api/admin-report.php?action=import_data&customer=${encodeURIComponent(customerId)}&t=${Date.now()}${vq}`).then((r) => r.json()).catch(() => null),
      adminFetch(`/api/admin-report.php?action=sales&customer=${encodeURIComponent(customerId)}&t=${Date.now()}${vq}`).then((r) => r.json()).catch(() => null),
    ]).then(([imp, sal]) => {
      setRows(imp && Array.isArray(imp.rows) ? imp.rows : []);
      setPl(sal && sal.pl && typeof sal.pl === 'object' ? sal.pl : {});
      setRates(sal && sal.rates && typeof sal.rates === 'object' ? sal.rates : {});
    }).finally(() => setLoading(false));
  }, [customerId, vq]);

  const company = customers.find((c) => c.id === customerId)?.name || '';

  // 勤怠取込から：賃金総額（＝人件費実績）・総労働時間・人数・退職
  const M = useMemo(() => {
    const out = {};
    (rows || []).forEach((r) => {
      const mk = monthKey(r['年月']);
      if (!/^\d{4}-\d{2}$/.test(mk)) return;
      if (!out[mk]) out[mk] = { wage: 0, hours: 0, full: 0, short: 0, retFull: 0, retShort: 0 };
      out[mk].wage += monthlyBase(r) + allowanceTotal(r) + overtimePay(r); // 基準外賃金（割増）も含む
      out[mk].hours += hm(r['総労働時間数']);
      if (isShortType(r['雇用形態'])) out[mk].short++; else out[mk].full++;
    });
    const seen = new Set();
    (rows || []).forEach((r) => {
      const k = (r['社員番号'] || '') + fullName(r);
      const rd = r['退職日'];
      if (!rd || seen.has(k)) return;
      const rmk = monthKey(rd);
      if (!/^\d{4}-\d{2}$/.test(rmk)) return;
      seen.add(k);
      if (!out[rmk]) out[rmk] = { wage: 0, hours: 0, full: 0, short: 0, retFull: 0, retShort: 0 };
      if (isShortType(r['雇用形態'])) out[rmk].retShort++; else out[rmk].retFull++;
    });
    return out;
  }, [rows]);

  const fyMonths = useMemo(() => Array.from({ length: 12 }, (_, i) => {
    const m0 = startMonth + i; const y = m0 <= 12 ? fy : fy + 1; const mm = ((m0 - 1) % 12) + 1;
    return { key: `${y}-${String(mm).padStart(2, '0')}`, label: `${mm}月` };
  }), [fy, startMonth]);

  const setPLv = (mk, field, vThousand) => setPl((prev) => {
    const cur = prev[mk] || {};
    const yen = vThousand === '' ? 0 : Math.round(Number(vThousand) * 1000);
    return { ...prev, [mk]: { ...cur, [field]: yen } };
  });

  // 月次の予実計算（円）。人件費予算＝限界利益−経費、人件費実績＝賃金総額。
  const rfy = rates[String(fy)] || {};
  const laborRate = Number(rfy.laborRate) || 0; // 労働分配率（予算・%）
  const prodTarget = Number(rfy.prodTarget) || 0; // 人時生産性 目標（円）
  const md = (mk) => {
    const p = pl[mk] || {};
    const bSales = Number(p.bSales) || 0, bVar = Number(p.bVar) || 0, bExp = Number(p.bExp) || 0, bProfit = Number(p.bProfit) || 0;
    const aSales = Number(p.aSales) || 0, aVar = Number(p.aVar) || 0, aExp = Number(p.aExp) || 0;
    const m = M[mk] || { wage: 0, hours: 0 };
    const bMargin = bSales - bVar, aMargin = aSales - aVar;
    // 予算人件費：予実モード＝粗利−経費−利益／人時生産性モード＝粗利×労働分配率予算%
    const bLabor = profit ? (bMargin - bExp - bProfit) : (laborRate > 0 ? Math.round(bMargin * laborRate / 100) : 0);
    const aLabor = m.wage;                 // 実績人件費＝賃金総額（基準外含む）
    const aProfit = aMargin - aExp - aLabor; // 実績利益＝粗利−経費−人件費
    return {
      bSales, bVar, bExp, bProfit, aSales, aVar, aExp, aProfit, bMargin, aMargin, bLabor, aLabor,
      diff: aLabor - bLabor,
      bShare: !profit && laborRate > 0 ? laborRate : (bMargin > 0 ? bLabor / bMargin * 100 : null),
      aShare: aMargin > 0 ? aLabor / aMargin * 100 : null,
      prod: m.hours > 0 ? aMargin / m.hours : null, // 人時生産性（円）＝実績粗利÷総労働時間
      hours: m.hours,
    };
  };

  const total = useMemo(() => {
    const ds = fyMonths.map((m) => md(m.key));
    const sum = (sel) => ds.reduce((s, d) => s + (sel(d) || 0), 0);
    const active = ds.filter((d) => d.aSales > 0); // 実績売上が入っている月だけ
    const sa = (sel) => active.reduce((s, d) => s + (sel(d) || 0), 0);
    const bMargin = sum((d) => d.bMargin), aMargin = sum((d) => d.aMargin);
    const bLabor = sum((d) => d.bLabor), aLabor = sum((d) => d.aLabor);
    const aMarginA = sa((d) => d.aMargin), hoursA = sa((d) => d.hours), aLaborA = sa((d) => d.aLabor);
    return {
      bSales: sum((d) => d.bSales), aSales: sum((d) => d.aSales), bVar: sum((d) => d.bVar), aVar: sum((d) => d.aVar),
      bExp: sum((d) => d.bExp), aExp: sum((d) => d.aExp), bProfit: sum((d) => d.bProfit), aProfit: sum((d) => d.aProfit),
      bMargin, aMargin, bLabor, aLabor, diff: aLabor - bLabor,
      bShare: bMargin > 0 ? bLabor / bMargin * 100 : null,
      aShare: aMarginA > 0 ? aLaborA / aMarginA * 100 : null,
      prod: hoursA > 0 ? aMarginA / hoursA : null, hours: hoursA,
    };
  }, [pl, M, fyMonths]); // eslint-disable-line react-hooks/exhaustive-deps

  // グラフ用：売上・粗利（予算/実績・円）と人時生産性（実績・円）
  const chartData = fyMonths.map((m) => { const d = md(m.key); return { label: m.label, bSales: d.bSales, aSales: d.aSales, bMargin: d.bMargin, aMargin: d.aMargin, prod: d.aSales > 0 ? (d.prod || 0) : null }; });
  // 年計の人時生産性 達成率（実績÷目標）
  const achv = (prodTarget > 0 && total.prod != null) ? total.prod / prodTarget * 100 : null;

  // 予算の率設定（年度ごと）
  const setRate = (field, v) => setRates((prev) => ({ ...prev, [String(fy)]: { ...(prev[String(fy)] || {}), [field]: v === '' ? '' : Number(v) } }));
  // 月平均売上→全月の予算売上、変動費率%→各月の予算変動費 を一括反映
  const applyBudget = () => {
    const avgYen = Math.round((Number(rfy.avgSales) || 0) * 1000);
    const vr = Number(rfy.varRate) || 0;
    setPl((prev) => {
      const next = { ...prev };
      fyMonths.forEach((m) => {
        const cur = { ...(next[m.key] || {}) };
        if (avgYen > 0) cur.bSales = avgYen;
        const base = Number(cur.bSales) || 0;
        if (vr > 0) cur.bVar = Math.round(base * vr / 100);
        next[m.key] = cur;
      });
      return next;
    });
  };

  const save = async () => {
    setSaving(true); setMsg('');
    const monthly = {};
    Object.keys(pl).forEach((mk) => { if (Number(pl[mk] && pl[mk].aSales) > 0) monthly[mk] = Number(pl[mk].aSales); });
    try {
      const res = await adminFetch('/api/admin-report.php', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'save_sales', customerId, pl, monthly, rates, ...(viewAs ? { view_as: viewAs } : {}) }),
      });
      const d = await res.json();
      setMsg(d.success ? '保存しました' : (d.message || '保存に失敗しました'));
      if (d.success) setTimeout(() => setMsg(''), 1500);
    } catch { setMsg('保存に失敗しました'); }
    setSaving(false);
  };

  // 予実表の行定義（各月＝予算/実績ペア。金額は千円、人時生産性は円、分配率は%）
  const PL_ROWS = [
    { k: '売上', bIn: 'bSales', aIn: 'aSales' },
    { k: '変動費', bIn: 'bVar', aIn: 'aVar' },
    { k: '粗利', bF: (d) => fmtK(d.bMargin), aF: (d) => fmtK(d.aMargin), bold: true, line: !profit },
    ...(profit ? [
      { k: '経費', bIn: 'bExp', aIn: 'aExp' },
      { k: '利益（予算は目標）', bIn: 'bProfit', aF: (d) => fmtK(d.aProfit), aClsOf: (d) => (d.aProfit < 0 ? 'text-rose-600' : 'text-gray-700'), line: true },
    ] : []),
    { k: '人件費', bF: (d) => fmtK(d.bLabor), aF: (d) => fmtK(d.aLabor), cls: 'text-indigo-600', bold: true },
    { k: '労働分配率', bF: (d) => (d.bShare != null ? `${d.bShare.toFixed(0)}%` : '—'), aF: (d) => (d.aShare != null ? `${d.aShare.toFixed(0)}%` : '—'), cls: 'text-amber-700' },
    { k: '人時生産性(円)', bF: () => (prodTarget > 0 ? fmtY(prodTarget) : '—'), aF: (d) => (d.prod != null ? fmtY(d.prod) : '—'), bold: true, aClsOf: (d) => (d.prod != null && prodTarget > 0 ? (d.prod >= prodTarget ? 'text-emerald-700' : 'text-rose-600') : 'text-emerald-700') },
  ];
  const halfMonths = half === 0 ? fyMonths.slice(0, 6) : fyMonths.slice(6, 12);

  // 印刷：年計サマリー＋表示中グラフ＋月次予実表（12ヶ月＋年計）を出力
  const printReport = () => {
    const esc = (s) => String(s ?? '').replace(/[&<>]/g, (m) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[m]));
    const svg = chartRef.current?.querySelector('svg');
    const TABS = ['①売上 × 人時生産性', '②粗利 × 人時生産性', '③売上・粗利 × 人時生産性'];
    const clsOf = (c) => (String(c || '').includes('rose') ? ' red' : String(c || '').includes('emerald') ? ' green' : '');
    const mTh = fyMonths.map((m) => `<th colspan="2">${esc(m.label)}</th>`).join('');
    const baTh = fyMonths.map(() => '<th class="s">予算</th><th class="s">実績</th>').join('');
    const body = PL_ROWS.map((row) => {
      const cells = fyMonths.map((m) => {
        const d = md(m.key);
        const bYen = pl[m.key] && pl[m.key][row.bIn];
        const aYen = pl[m.key] && pl[m.key][row.aIn];
        const b = row.bIn ? (bYen ? fmtK(bYen) : '') : row.bF(d);
        const a = row.aIn ? (aYen ? fmtK(aYen) : '') : row.aF(d);
        return `<td class="num">${b}</td><td class="num${row.aClsOf ? clsOf(row.aClsOf(d)) : ''}">${a}</td>`;
      }).join('');
      const tb = row.bIn ? fmtK(total[row.bIn]) : row.bF(total);
      const ta = row.aIn ? fmtK(total[row.aIn]) : row.aF(total);
      return `<tr${row.bold ? ' class="bold"' : ''}><td class="lab">${esc(row.k)}</td>${cells}<td class="num tot">${tb}</td><td class="num tot${row.aClsOf ? clsOf(row.aClsOf(total)) : ''}">${ta}</td></tr>`;
    }).join('');
    const html = `<!doctype html><html lang="ja"><head><meta charset="utf-8"><title>${esc(title)}レポート</title>`
      + `<style>`
      // A4横・1ページに収めるためのコンパクトレイアウト（グラフは高さ固定で縮小）
      + `body{font-family:"Hiragino Kaku Gothic ProN","Yu Gothic",sans-serif;color:#222;margin:16px;}`
      + `h1{font-size:16px;margin:0 0 2px;}.sub{color:#666;font-size:10px;margin-bottom:8px;}`
      + `h2{font-size:12px;border-bottom:2px solid #ddd;padding-bottom:2px;margin:10px 0 5px;}`
      + `.kpis{display:flex;gap:8px;flex-wrap:wrap;}.kpi{border:1px solid #ddd;border-radius:8px;padding:4px 10px;}.kpi .l{font-size:9px;color:#666;}.kpi .v{font-size:13px;font-weight:bold;}`
      + `svg{display:block;margin:0 auto;height:158px;max-height:158px!important;width:auto;max-width:100%;}`
      + `table{border-collapse:collapse;font-size:8.5px;width:100%;}th,td{border:1px solid #ddd;padding:1.5px 3px;}th{background:#f3f3f3;}th.s{font-weight:normal;color:#888;font-size:7.5px;}`
      + `td.num{text-align:right;white-space:nowrap;}td.lab{text-align:left;white-space:nowrap;}td.tot{background:#fafafa;font-weight:bold;}tr.bold td{font-weight:bold;}`
      + `.red{color:#e11d48;}.green{color:#047857;}.note{font-size:9px;color:#666;margin-top:3px;}`
      + `@page{size:A4 landscape;margin:0;}@media print{body{margin:8mm;}*{-webkit-print-color-adjust:exact;print-color-adjust:exact;}}`
      + `</style></head><body>`
      + `<h1>${esc(title)}レポート</h1>`
      + `<div class="sub">${esc(company)}　／　${fy}年度（${startMonth}月開始）　／　出力 ${esc(new Date().toLocaleDateString('ja-JP'))}</div>`
      + `<h2>年計（${fy}年度）人時生産性</h2><div class="kpis">`
      + `<div class="kpi"><div class="l">人時生産性 予算（目標）</div><div class="v">${prodTarget > 0 ? fmtY(prodTarget) : '—'} 円</div></div>`
      + `<div class="kpi"><div class="l">人時生産性 実績</div><div class="v">${total.prod != null ? fmtY(total.prod) : '—'} 円</div></div>`
      + `<div class="kpi"><div class="l">達成率（実績÷目標）</div><div class="v${achv != null ? (achv >= 100 ? ' green' : ' red') : ''}">${achv == null ? '—' : achv.toFixed(0) + '%'}</div></div></div>`
      + (achv != null ? `<div class="note">${achv >= 100 ? '※ 人時生産性は目標を達成しています。' : `※ 人時生産性が目標を ${(100 - achv).toFixed(0)}% 下回っています。生産性改善が必要です。`}</div>` : '')
      + (svg ? `<h2>グラフ（${esc(TABS[chartTab])}）</h2>${svg.outerHTML}` : '')
      + `<h2>月次 予実（${fy}年度・${startMonth}月開始／金額＝千円・人時生産性＝円）</h2>`
      + `<table><thead><tr><th rowspan="2" class="lab">項目</th>${mTh}<th colspan="2">年計</th></tr><tr>${baTh}<th class="s">予算</th><th class="s">実績</th></tr></thead><tbody>${body}</tbody></table>`
      + `</body></html>`;
    printHtml(html);
  };

  return (
    <div>
      <h2 className="text-2xl font-bold mb-1">{title}</h2>
      <p className="text-xs text-gray-500 mb-6">{profit
        ? '売上・変動費・経費・利益（予算）を入力すると、人件費 予算＝粗利−経費−利益 を算出。実績の人件費＝勤怠の賃金総額（基準外含む）、実績利益＝粗利−経費−人件費。金額は千円、人時生産性は円。'
        : '売上・変動費（予算/実績）から粗利を、人件費（予算＝粗利×労働分配率予算%／実績＝勤怠の賃金総額・基準外含む）・人時生産性（粗利÷総労働時間）を算出します。金額は千円、人時生産性は円。'}</p>

      {!customerId && <div className="bg-white rounded-xl shadow p-8 text-center text-gray-400 mb-6">左メニュー下部の「顧客」から選択してください。</div>}
      {customerId && <div className="bg-white rounded-xl shadow p-5 mb-6 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <button onClick={() => setFy(fy - 1)} className="px-2 py-1 bg-gray-100 rounded hover:bg-gray-200 text-sm">◀</button>
          <span className="font-bold text-gray-800 w-24 text-center">{fy}年度</span>
          <button onClick={() => setFy(fy + 1)} className="px-2 py-1 bg-gray-100 rounded hover:bg-gray-200 text-sm">▶</button>
        </div>
        {msg && <span className="text-sm text-green-600">{msg}</span>}
        {customerId && <button onClick={save} disabled={saving} className="ml-auto bg-indigo-600 text-white px-5 py-2 rounded-lg text-sm font-bold hover:bg-indigo-700 disabled:opacity-50">{saving ? '保存中…' : '予算・実績を保存'}</button>}
        {customerId && <button onClick={printReport} disabled={loading}
          className="bg-gray-800 text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-gray-900 disabled:opacity-40">PDF / 印刷</button>}
      </div>}

      {loading && <div className="text-gray-500">読み込み中…</div>}
      {!loading && customerId && (
        <>
          {/* 年計サマリー */}
          <section className="bg-white rounded-xl shadow p-6 mb-6">
            <h3 className="font-bold text-gray-800 mb-4">年計（{fy}年度）人時生産性{company && <span className="text-sm text-gray-500 ml-2">{company}</span>}</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="border border-gray-100 rounded-xl px-4 py-3"><div className="text-xs text-gray-500">人時生産性 予算（目標）</div><div className="text-2xl font-bold mt-1 text-gray-800">{prodTarget > 0 ? fmtY(prodTarget) : '—'}<span className="text-xs font-normal text-gray-400 ml-1">円</span></div></div>
              <div className="border border-gray-100 rounded-xl px-4 py-3"><div className="text-xs text-gray-500">人時生産性 実績</div><div className="text-2xl font-bold mt-1 text-purple-700">{total.prod != null ? fmtY(total.prod) : '—'}<span className="text-xs font-normal text-gray-400 ml-1">円</span></div></div>
              <div className="border border-gray-100 rounded-xl px-4 py-3"><div className="text-xs text-gray-500">達成率（実績÷目標）</div><div className={`text-2xl font-bold mt-1 ${achv == null ? 'text-gray-400' : achv >= 100 ? 'text-emerald-600' : 'text-rose-600'}`}>{achv == null ? '—' : `${achv.toFixed(0)}%`}</div></div>
            </div>
            {achv != null && achv < 100 && <p className="text-xs text-rose-600 mt-3">※ 人時生産性が目標を {(100 - achv).toFixed(0)}% 下回っています。生産性改善が必要です。</p>}
            {achv != null && achv >= 100 && <p className="text-xs text-emerald-600 mt-3">※ 人時生産性は目標を達成しています。</p>}
          </section>

          {/* 予算の一括入力 */}
          <section className="bg-white rounded-xl shadow p-6 mb-6">
            <h3 className="font-bold text-gray-800 mb-1">予算の一括入力（{fy}年度）</h3>
            <p className="text-[11px] text-gray-400 mb-4">月平均売上→全月の予算売上に反映／変動費率→各月の予算変動費＝売上×率／労働分配率→予算人件費＝粗利×率。</p>
            <div className="flex flex-wrap items-end gap-4">
              <label className="flex flex-col text-xs text-gray-500">月平均売上（千円）
                <input type="number" value={rfy.avgSales ?? ''} onChange={(e) => setRate('avgSales', e.target.value)} className="mt-1 w-32 border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-400" /></label>
              <label className="flex flex-col text-xs text-gray-500">変動費率（%）
                <input type="number" value={rfy.varRate ?? ''} onChange={(e) => setRate('varRate', e.target.value)} className="mt-1 w-24 border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-400" /></label>
              {!profit && <label className="flex flex-col text-xs text-gray-500">労働分配率 予算（%）
                <input type="number" value={rfy.laborRate ?? ''} onChange={(e) => setRate('laborRate', e.target.value)} className="mt-1 w-28 border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-400" /></label>}
              <label className="flex flex-col text-xs text-gray-500">人時生産性 目標（円）
                <input type="number" value={rfy.prodTarget ?? ''} onChange={(e) => setRate('prodTarget', e.target.value)} className="mt-1 w-32 border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-emerald-400" /></label>
              <button onClick={applyBudget} className="bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-indigo-700">予算売上・変動費に反映</button>
            </div>
          </section>

          {/* グラフ：タブ（①売上 ②粗利 ③売上・粗利）×（各＋人時生産性）・予算/実績 */}
          {(() => {
            const SERIES = [
              [{ k: 'bSales', c: '#bfdbfe', n: '売上 予算' }, { k: 'aSales', c: '#2563eb', n: '売上 実績' }],
              [{ k: 'bMargin', c: '#a7f3d0', n: '粗利 予算' }, { k: 'aMargin', c: '#059669', n: '粗利 実績' }],
              [{ k: 'bSales', c: '#bfdbfe', n: '売上 予算' }, { k: 'aSales', c: '#2563eb', n: '売上 実績' }, { k: 'bMargin', c: '#a7f3d0', n: '粗利 予算' }, { k: 'aMargin', c: '#059669', n: '粗利 実績' }],
            ];
            const TABS = ['①売上 × 人時生産性', '②粗利 × 人時生産性', '③売上・粗利 × 人時生産性'];
            const series = SERIES[chartTab];
            const W = 760, H = 250, pl0 = 8, pr0 = 8, pt = 14, pb = 22;
            const iw = W - pl0 - pr0, ih = H - pt - pb;
            const maxM = Math.max(1, ...chartData.flatMap((d) => series.map((s) => d[s.k] || 0)));
            const maxP = Math.max(1, prodTarget, ...chartData.map((d) => d.prod || 0));
            const n = chartData.length || 1;
            const bw = iw / n;
            const k = series.length;
            const gW = bw * 0.82, barW = gW / k, gx = bw * 0.09;
            const xx = (i) => pl0 + bw * i;
            const yM = (v) => pt + ih - (v / maxM) * ih;
            const yP = (v) => pt + ih - (v / maxP) * ih;
            const linePts = chartData.map((d, i) => (d.prod != null ? `${xx(i) + bw / 2},${yP(d.prod)}` : null)).filter(Boolean).join(' ');
            return (
              <section ref={chartRef} className="bg-white rounded-xl shadow p-6 mb-6">
                <div className="flex items-center flex-wrap gap-2 mb-3">
                  <h3 className="font-bold text-gray-800">グラフ（{fy}年度）</h3>
                  <div className="flex items-center gap-1 ml-auto">
                    {TABS.map((t, i) => (
                      <button key={i} onClick={() => setChartTab(i)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-bold ${chartTab === i ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>{t}</button>
                    ))}
                  </div>
                </div>
                <div className="flex gap-4 flex-wrap text-[11px] text-gray-500 mb-2">
                  {series.map((s) => <span key={s.k} className="flex items-center gap-1"><span className="inline-block w-3 h-3" style={{ background: s.c }} />{s.n}(千円)</span>)}
                  <span className="flex items-center gap-1"><span className="inline-block w-4 h-0.5 bg-purple-600" />人時生産性 実績(円)</span>
                  {prodTarget > 0 && <span className="flex items-center gap-1"><span className="inline-block w-4 border-t-2 border-dashed border-red-600" />人時生産性 目標(円)</span>}
                </div>
                <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ maxHeight: 270 }}>
                  {chartData.map((d, i) => (
                    <g key={i}>
                      {series.map((s, j) => {
                        const v = d[s.k] || 0;
                        return <rect key={s.k} x={xx(i) + gx + barW * j} y={yM(v)} width={barW * 0.9} height={pt + ih - yM(v)} fill={s.c} />;
                      })}
                      <text x={xx(i) + bw / 2} y={H - 7} textAnchor="middle" fontSize="9" fill="#9ca3af">{d.label}</text>
                    </g>
                  ))}
                  {prodTarget > 0 && <line x1={pl0} y1={yP(prodTarget)} x2={W - pr0} y2={yP(prodTarget)} stroke="#dc2626" strokeWidth="1.5" strokeDasharray="5 4" />}
                  {prodTarget > 0 && <text x={W - pr0} y={yP(prodTarget) + 11} textAnchor="end" fontSize="9" fill="#dc2626">目標 {fmtY(prodTarget)}円</text>}
                  <polyline points={linePts} fill="none" stroke="#9333ea" strokeWidth="2" />
                  {chartData.map((d, i) => (d.prod != null ? <circle key={i} cx={xx(i) + bw / 2} cy={yP(d.prod)} r="2.5" fill="#9333ea" /> : null))}
                </svg>
                <p className="text-[11px] text-gray-400 mt-1">棒＝{TABS[chartTab].replace(/^[①②③]/, '').split(' × ')[0]}（左目盛・相対）、紫線＝人時生産性 実績、赤破線＝目標（右目盛・相対）。</p>
              </section>
            );
          })()}

          {/* 月次 予実表（各月＝予算/実績） */}
          <section className="bg-white rounded-xl shadow p-6 mb-6">
            <div className="flex items-center flex-wrap gap-2 mb-3">
              <h3 className="font-bold text-gray-800">月次 予実（{fy}年度・{startMonth}月開始）</h3>
              <span className="text-[11px] text-gray-400">金額＝千円／白セルは入力（売上・変動費・経費）。人件費実績・総労働時間は勤怠から自動。</span>
              <div className="flex items-center gap-1 ml-auto">
                {[[0, '前半6ヶ月'], [1, '後半6ヶ月']].map(([k, lbl]) => (
                  <button key={k} onClick={() => setHalf(k)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold ${half === k ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>{lbl}</button>
                ))}
              </div>
            </div>
            <div className="overflow-auto">
              <table className="w-full text-[11px]">
                <thead>
                  <tr className="text-gray-500">
                    <th rowSpan={2} className="px-2 py-1 text-left bg-gray-50 sticky left-0 align-bottom">項目（千円）</th>
                    {halfMonths.map((m) => <th key={m.key} colSpan={2} className="px-2 py-1 text-center border-l border-gray-200">{m.label}</th>)}
                    <th colSpan={2} className="px-2 py-1 text-center border-l-2 border-gray-300 bg-gray-50">年計</th>
                  </tr>
                  <tr className="text-gray-400 text-[10px]">
                    {halfMonths.map((m) => (
                      <React.Fragment key={m.key}>
                        <th className="px-2 py-0.5 text-right border-l border-gray-200">予算</th>
                        <th className="px-2 py-0.5 text-right">実績</th>
                      </React.Fragment>
                    ))}
                    <th className="px-2 py-0.5 text-right border-l-2 border-gray-300 bg-gray-50">予算</th>
                    <th className="px-2 py-0.5 text-right bg-gray-50">実績</th>
                  </tr>
                </thead>
                <tbody>
                  {PL_ROWS.map((row) => {
                    const ln = (row.line ? ' border-b-2 border-gray-400' : '') + (row.topline ? ' border-t-2 border-gray-400' : '');
                    return (
                    <tr key={row.k} className="border-t border-gray-50">
                      <td className={`px-2 py-1 text-left text-gray-700 bg-white sticky left-0 whitespace-nowrap ${row.bold ? 'font-bold' : ''}${ln}`}>{row.k}</td>
                      {halfMonths.map((m) => {
                        const d = md(m.key);
                        const bYen = pl[m.key] && pl[m.key][row.bIn];
                        const aYen = pl[m.key] && pl[m.key][row.aIn];
                        return (
                          <React.Fragment key={m.key}>
                            <td className={`px-1 py-0.5 text-right border-l border-gray-100${ln}`}>
                              {row.bIn
                                ? <input type="number" value={bYen ? Math.round(bYen / 1000) : ''} onChange={(e) => setPLv(m.key, row.bIn, e.target.value)} className="w-14 text-right border border-gray-200 rounded px-1 py-0.5 text-[11px] focus:outline-none focus:ring-1 focus:ring-indigo-400" />
                                : <span className={`tabular-nums ${row.cls || ''} ${row.bold ? 'font-bold' : ''}`}>{row.bF(d)}</span>}
                            </td>
                            <td className={`px-1 py-0.5 text-right bg-indigo-50/30${ln}`}>
                              {row.aIn
                                ? <input type="number" value={aYen ? Math.round(aYen / 1000) : ''} onChange={(e) => setPLv(m.key, row.aIn, e.target.value)} className="w-14 text-right border border-gray-200 rounded px-1 py-0.5 text-[11px] focus:outline-none focus:ring-1 focus:ring-indigo-400" />
                                : <span className={`tabular-nums ${row.cls || ''} ${row.aClsOf ? row.aClsOf(d) : ''} ${row.bold ? 'font-bold' : ''}`}>{row.aF(d)}</span>}
                            </td>
                          </React.Fragment>
                        );
                      })}
                      <td className={`px-2 py-1 text-right tabular-nums border-l-2 border-gray-300 bg-gray-50 ${row.cls || ''} ${row.bold ? 'font-bold' : ''}${ln}`}>{row.bIn ? fmtK(total[row.bIn]) : row.bF(total)}</td>
                      <td className={`px-2 py-1 text-right tabular-nums bg-gray-50 ${row.cls || ''} ${row.aClsOf ? row.aClsOf(total) : ''} ${row.bold ? 'font-bold' : ''}${ln}`}>{row.aIn ? fmtK(total[row.aIn]) : row.aF(total)}</td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>

        </>
      )}
    </div>
  );
};

// ====================== ルート ======================
// ====================== 適正賃金分析（他社比較） ======================
// 勤続年数バケット（派遣賃金 別添2 の列に対応）
const TENURE_BUCKETS = [
  { key: 'base_0y', label: '0年', y: 0 }, { key: 'y1', label: '1年', y: 1 }, { key: 'y3', label: '3年', y: 3 },
  { key: 'y5', label: '5年', y: 5 }, { key: 'y10', label: '10年', y: 10 }, { key: 'y20', label: '20年', y: 20 },
];
// 社員識別キー（サーバーの emp_key と一致：社員番号__姓名）
const jsEmpKey = (r) => `${String(r['社員番号'] || '').trim()}__${String(r['氏名（姓）'] || '').trim()}${String(r['氏名（名）'] || '').trim()}`;
const tenureYears = (hireDate, ym) => {
  const h2 = String(hireDate || '').match(/(\d{4})\D+(\d{1,2})/);
  const tm = String(ym || '').match(/(\d{4})\D+(\d{1,2})/);
  if (!h2 || !tm) return null;
  let m = (parseInt(tm[1]) - parseInt(h2[1])) * 12 + (parseInt(tm[2]) - parseInt(h2[2]));
  return m < 0 ? 0 : Math.floor(m / 12);
};
// 2次の最小二乗（Y = c0 + c1·X + c2·X²）。係数 [c0,c1,c2] を返す（解けない時 null）。
const quadFit = (xs, ys) => {
  const n = xs.length;
  if (n < 3) return null;
  let Sx = 0, Sxx = 0, Sxxx = 0, Sxxxx = 0, Sy = 0, Sxy = 0, Sxxy = 0;
  for (let i = 0; i < n; i++) { const x = xs[i], y = ys[i], x2 = x * x; Sx += x; Sxx += x2; Sxxx += x2 * x; Sxxxx += x2 * x2; Sy += y; Sxy += x * y; Sxxy += x2 * y; }
  const M = [[n, Sx, Sxx], [Sx, Sxx, Sxxx], [Sxx, Sxxx, Sxxxx]];
  const V = [Sy, Sxy, Sxxy];
  const det3 = (m) => m[0][0] * (m[1][1] * m[2][2] - m[1][2] * m[2][1]) - m[0][1] * (m[1][0] * m[2][2] - m[1][2] * m[2][0]) + m[0][2] * (m[1][0] * m[2][1] - m[1][1] * m[2][0]);
  const D = det3(M);
  if (!D) return null;
  const col = (m, c, v) => m.map((row, i) => row.map((val, j) => (j === c ? v[i] : val)));
  return [det3(col(M, 0, V)) / D, det3(col(M, 1, V)) / D, det3(col(M, 2, V)) / D];
};
// 所定内賃金の月給換算（基本給A＋所定内手当 時給B/日額C/月額D。手当は基準内「内」のみ）
const seiteinaiMonthly = (r) => {
  if (!r) return 0;
  const mph = num(r['月平均所定労働時間']);  // 月平均所定労働時間（時間）
  const hpd = hm(r['所定労働時間/日']);       // 所定労働時間/日（時間）
  const type = r['基本給の種別'] || '';
  let A = 0;
  if (type.includes('時給')) A = num(r['基本給（時給）']) * mph;
  else if (type.includes('日給')) A = hpd > 0 ? num(r['基本給（日給）']) / hpd * mph : 0;
  else if (type.includes('月給')) A = num(r['基本給（月給）']);
  else if (type.includes('年俸')) A = num(r['基本給（年俸制）']) / 12;
  else A = num(r['基本給（月給）']) || num(r['基本給（時給）']) * mph;
  return A + kijunNaiMonthly(r); // 基本給＋基準内（「内」）手当の月額換算
};

// ====================== 賃金散布図（会社全体・勤続年数×月額賃金） ======================
const WageScatter = ({ customers, viewAs, customerId = '' }) => {
  const [emps, setEmps] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [xMode, setXMode] = useState('tenure'); // staff / age / tenure / all
  const [regType, setRegType] = useState('line'); // none / line / curve
  const [aiEval, setAiEval] = useState(null);
  const [aiGen, setAiGen] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiErr, setAiErr] = useState('');
  const chartRef = useRef(null);
  const vq = viewAs ? `&view_as=${encodeURIComponent(viewAs)}` : '';

  // 顧客切替時に保存済みAI分析を読み込み
  useEffect(() => {
    setAiEval(null); setAiGen(''); setAiErr('');
    if (!customerId) return;
    adminFetch(`/api/admin-report.php?action=scatter_saved&customer=${encodeURIComponent(customerId)}&t=${Date.now()}${vq}`)
      .then((r) => r.json()).then((d) => { if (d && d.eval) { setAiEval(d.eval); setAiGen(d.generatedAt || ''); } }).catch(() => {});
  }, [customerId, vq]);

  useEffect(() => {
    if (!customerId) { setEmps([]); setRows([]); return; }
    setLoading(true);
    Promise.all([
      adminFetch(`/api/admin-report.php?action=employees&customer=${encodeURIComponent(customerId)}&t=${Date.now()}${vq}`).then((r) => r.json()).catch(() => []),
      adminFetch(`/api/admin-report.php?action=import_data&customer=${encodeURIComponent(customerId)}&t=${Date.now()}${vq}`).then((r) => r.json()).catch(() => null),
    ]).then(([e, imp]) => {
      setEmps(Array.isArray(e) ? e : []);
      setRows(imp && Array.isArray(imp.rows) ? imp.rows : []);
    }).finally(() => setLoading(false));
  }, [customerId, vq]);

  // 全社員：月額賃金（月給換算）＋ スタッフ番号 / 年齢 / 勤続年数。（レポート除外の社員は外す）
  const empPoints = useMemo(() => emps.filter((e) => !e.excluded).map((e, idx) => {
    const mine = rows.filter((r) => jsEmpKey(r) === e.key);
    const sorted = mine.length ? [...mine].sort((a, b) => monthKey(b['年月']).localeCompare(monthKey(a['年月']))) : [];
    const row = sorted.find((r) => num(r['月平均所定労働時間']) > 0) || sorted[0] || null;
    const staffNo = Number(e.empNo);
    return {
      name: e.name || e.empNo || '—',
      y: seiteinaiMonthly(row),
      staff: isNaN(staffNo) ? idx + 1 : staffNo,
      age: ageOf(e.birth, e.lastMonth),
      tenure: tenureYears(e.hireDate, e.lastMonth),
      gender: row ? String(row['性別'] || '').trim() : '',
    };
  }), [emps, rows]);

  const X_MODES = { staff: 'スタッフ（社員番号）', age: '年齢（歳）', tenure: '勤続年数（年）', all: '各指標（左＝小 → 右＝大）' };
  const getX = (p) => (xMode === 'staff' ? p.staff : xMode === 'age' ? p.age : xMode === 'tenure' ? p.tenure : null);
  const shown = empPoints.filter((p) => p.y > 0 && (xMode === 'all' || getX(p) != null));
  // 点は実際の位置に置く（横ずらしなし）。重なりは半透明で表現。（単一指標時）
  const plotted = shown.map((p) => { const x = getX(p); return { ...p, x, px: x }; });

  const runAi = async () => {
    if (!customerId || shown.length === 0) return;
    setAiLoading(true); setAiErr('');
    try {
      const company = customers.find((c) => c.id === customerId)?.name || '';
      const points = shown.map((p) => ({ name: p.name, gender: p.gender, age: p.age, tenure: p.tenure, wage: Math.round(p.y) }));
      const res = await adminFetch('/api/admin-report.php', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'scatter_eval', customerId, companyName: company, points, ...(viewAs ? { view_as: viewAs } : {}) }),
      });
      const d = await res.json();
      if (d.success) { setAiEval(d.eval); setAiGen(d.generatedAt || ''); } else setAiErr(d.message || 'AI分析に失敗しました');
    } catch { setAiErr('AI分析に失敗しました'); }
    setAiLoading(false);
  };

  // 印刷：画面表示中の散布図SVG（横軸・回帰の設定そのまま）＋AI分析を出力
  const printReport = () => {
    const svg = chartRef.current?.querySelector('svg');
    if (!svg) return;
    const esc = (s) => String(s ?? '').replace(/[&<>]/g, (m) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[m]));
    const company = customers.find((c) => c.id === customerId)?.name || '';
    const avgY = shown.length ? shown.reduce((s, p) => s + p.y, 0) / shown.length : 0;
    const legend = xMode === 'all'
      ? '<span class="dot" style="background:#3b82f6"></span>スタッフ　<span class="dot" style="background:#10b981"></span>年齢　<span class="dot" style="background:#f59e0b"></span>勤続年数'
      : '<span class="dot" style="background:#2563eb"></span>男性　<span class="dot" style="background:#ec4899"></span>女性';
    let aiHtml = '';
    if (aiEval) {
      const list = (arr) => (arr || []).map((t) => `<li>${esc(t)}</li>`).join('');
      aiHtml = `<h2>AIによる分析${aiGen ? ` <span class="meta">（生成：${esc(new Date(aiGen).toLocaleString('ja-JP'))}）</span>` : ''}</h2>`
        + (aiEval.overview ? `<p class="ov">${esc(aiEval.overview)}</p>` : '')
        + `<div class="cols"><div class="box good"><h3>特徴・良い点</h3><ul>${list(aiEval.findings)}</ul></div>`
        + `<div class="box risk"><h3>注意点・リスク</h3><ul>${list(aiEval.risks)}</ul></div>`
        + `<div class="box sug"><h3>改善提案</h3><ul>${list(aiEval.suggestions)}</ul></div></div>`;
    }
    const html = `<!doctype html><html lang="ja"><head><meta charset="utf-8"><title>賃金散布図レポート</title>`
      + `<style>`
      + `body{font-family:"Hiragino Kaku Gothic ProN","Yu Gothic",sans-serif;color:#222;margin:24px;}`
      + `h1{font-size:20px;margin:0 0 4px;}.sub{color:#666;font-size:12px;margin-bottom:14px;}`
      + `h2{font-size:15px;border-bottom:2px solid #ddd;padding-bottom:4px;margin:22px 0 10px;}h3{font-size:13px;margin:0 0 6px;}`
      + `.legend{font-size:11px;color:#555;margin-bottom:6px;}.dot{display:inline-block;width:9px;height:9px;border-radius:50%;margin-right:3px;vertical-align:middle;}`
      + `svg{width:100%;height:auto;}`
      + `.ov{background:#eef2ff;border-radius:6px;padding:8px 12px;font-size:12px;}`
      + `.cols{display:flex;gap:12px;}.box{flex:1;border-radius:8px;padding:10px 14px;}.box.good{background:#ecfdf3;}.box.risk{background:#fef2f4;}.box.sug{background:#fffbeb;}`
      + `.box ul{margin:6px 0 0;padding-left:18px;}.box li{margin:4px 0;font-size:12px;}.meta{color:#999;font-weight:normal;font-size:11px;}`
      + `@page{margin:0;}@media print{body{margin:12mm;}*{-webkit-print-color-adjust:exact;print-color-adjust:exact;}}`
      + `</style></head><body>`
      + `<h1>賃金散布図レポート</h1>`
      + `<div class="sub">${esc(company)}　／　横軸：${esc(X_MODES[xMode])}　／　対象 ${shown.length} 名　／　平均 ¥${fmtY(avgY)}　／　出力 ${esc(new Date().toLocaleDateString('ja-JP'))}</div>`
      + `<h2>散布図（縦軸＝月額賃金・所定内月給換算）</h2>`
      + `<div class="legend">${legend}　<span style="color:#9333ea">- - 平均線</span></div>`
      + svg.outerHTML
      + aiHtml
      + `</body></html>`;
    printHtml(html);
  };

  return (
    <div>
      <h2 className="text-2xl font-bold mb-1">賃金散布図</h2>
      <p className="text-xs text-gray-500 mb-6">会社の全従業員を、縦軸＝月額賃金（所定内・月給換算）でプロットします。横軸はスタッフ／年齢／勤続年数で切り替えできます。</p>

      {!customerId && <div className="bg-white rounded-xl shadow p-8 text-center text-gray-400 mb-6">左メニュー下部の「顧客」から選択してください。</div>}
      {customerId && <div className="bg-white rounded-xl shadow p-5 mb-6 flex flex-wrap items-center gap-3">
        {!loading && <span className="text-xs text-gray-500">対象 {shown.length} 名{empPoints.length !== shown.length ? `（${empPoints.length - shown.length} 名は対象外）` : ''}</span>}
        <div className="flex items-center gap-1 ml-auto flex-wrap">
          <span className="text-xs text-gray-400 mr-1">横軸</span>
          {[['staff', 'スタッフ'], ['age', '年齢'], ['tenure', '勤続年数'], ['all', '全体']].map(([k, lbl]) => (
            <button key={k} onClick={() => setXMode(k)}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold ${xMode === k ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>{lbl}</button>
          ))}
          <span className="text-xs text-gray-400 ml-3 mr-1">回帰</span>
          {[['none', 'なし'], ['line', '直線'], ['curve', '曲線']].map(([k, lbl]) => (
            <button key={k} onClick={() => setRegType(k)} disabled={xMode === 'all'}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold disabled:opacity-40 ${regType === k ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>{lbl}</button>
          ))}
          <button onClick={printReport} disabled={shown.length === 0}
            className="ml-3 bg-gray-800 text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-gray-900 disabled:opacity-40">PDF / 印刷</button>
        </div>
      </div>}

      {loading && <div className="text-gray-500">読み込み中…</div>}
      {customerId && !loading && shown.length === 0 && (
        <div className="bg-white rounded-xl shadow p-8 text-center text-gray-400">この顧客の取込データがありません。「CSV取り込み」から保存してください。</div>
      )}
      {!loading && shown.length > 0 && (() => {
        const W = 900, H = 420, padL = 64, padR = 20, padT = 16, padB = 44;
        const plotW = W - padL - padR, plotH = H - padT - padB;
        const yMax = Math.max(100000, Math.ceil(Math.max(...shown.map((p) => p.y)) / 100000) * 100000);
        const yPos = (v) => padT + plotH - (v / yMax) * plotH;
        const yTicks = []; for (let t = 0; t <= yMax; t += 100000) yTicks.push(t);
        const axis = (
          <>
            {yTicks.map((t) => (
              <g key={`y${t}`}>
                <line x1={padL} y1={yPos(t)} x2={W - padR} y2={yPos(t)} stroke="#eef2f7" />
                <text x={padL - 6} y={yPos(t) + 3} fontSize="10" fill="#9ca3af" textAnchor="end">{t / 10000}万</text>
              </g>
            ))}
            <line x1={padL} y1={padT} x2={padL} y2={padT + plotH} stroke="#d1d5db" />
            <line x1={padL} y1={padT + plotH} x2={W - padR} y2={padT + plotH} stroke="#d1d5db" />
            <text x={14} y={padT + plotH / 2} fontSize="10" fill="#6b7280" textAnchor="middle" transform={`rotate(-90 14 ${padT + plotH / 2})`}>月額賃金（万円）</text>
          </>
        );
        const avgY = shown.reduce((s, p) => s + p.y, 0) / shown.length;
        const avgLine = (
          <>
            <line x1={padL} y1={yPos(avgY)} x2={W - padR} y2={yPos(avgY)} stroke="#9333ea" strokeWidth="1.5" strokeDasharray="5 4" />
            <text x={W - padR} y={yPos(avgY) - 3} fontSize="9" fill="#9333ea" textAnchor="end">平均 ¥{fmtY(avgY)}</text>
          </>
        );

        // 全体：3指標を色分けで重ね表示（各指標を 左=小→右=大 に正規化）
        if (xMode === 'all') {
          const series = [
            { key: 'staff', label: 'スタッフ', color: '#3b82f6', tip: (p) => `No.${p.staff}` },
            { key: 'age', label: '年齢', color: '#10b981', tip: (p) => `${p.age}歳` },
            { key: 'tenure', label: '勤続年数', color: '#f59e0b', tip: (p) => `勤続${p.tenure}年` },
          ];
          const allVals = series.flatMap((s) => shown.map((p) => p[s.key]).filter((v) => v != null));
          const xMax = Math.max(5, ...allVals);
          const xPos = (v) => padL + (v / xMax) * plotW;
          const xStep = xMax <= 12 ? 1 : Math.ceil(xMax / 12);
          const xTicks = []; for (let t = 0; t <= xMax; t += xStep) xTicks.push(t);
          return (
            <div ref={chartRef} className="bg-white rounded-xl shadow p-6">
              <div className="flex flex-wrap items-center gap-4 mb-3 text-[11px] text-gray-600">
                {series.map((s) => <span key={s.key} className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-full" style={{ background: s.color }} />{s.label}</span>)}
                <span className="flex items-center gap-1"><span className="inline-block w-4 border-t-2 border-dashed" style={{ borderColor: '#9333ea' }} />平均 ¥{fmtY(avgY)}</span>
                <span className="text-gray-400">実値のまま重ね表示</span>
              </div>
              <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ maxWidth: '100%' }}>
                {axis}
                {xTicks.map((t) => (
                  <text key={`x${t}`} x={xPos(t)} y={H - padB + 16} fontSize="10" fill="#9ca3af" textAnchor="middle">{t}</text>
                ))}
                <text x={padL + plotW / 2} y={H - 6} fontSize="10" fill="#6b7280" textAnchor="middle">値（社員番号 / 歳 / 年）</text>
                {avgLine}
                {series.map((s) => shown.filter((p) => p[s.key] != null).map((p, i) => (
                  <circle key={s.key + i} cx={xPos(p[s.key])} cy={yPos(p.y)} r="5" fill={s.color} fillOpacity="0.6">
                    <title>{`${p.name}：${s.tip(p)} / ${fmtY(p.y)}円`}</title>
                  </circle>
                )))}
              </svg>
            </div>
          );
        }

        // 単一指標
        const xsAll = shown.map(getX);
        let xMin = 0, xMax;
        if (xMode === 'age') {
          xMin = Math.max(0, Math.floor(Math.min(...xsAll) / 5) * 5 - 5);
          xMax = Math.ceil(Math.max(...xsAll) / 5) * 5;
        } else {
          xMax = Math.max(xMode === 'staff' ? 1 : 5, ...plotted.map((p) => p.px));
        }
        if (xMax <= xMin) xMax = xMin + 5;
        const xPos = (v) => padL + ((v - xMin) / (xMax - xMin)) * plotW;
        const span = xMax - xMin;
        const xStep = span <= 12 ? 1 : Math.ceil(span / 12);
        const xTicks = []; for (let t = xMin; t <= xMax; t += xStep) xTicks.push(t);
        const tip = (p) => xMode === 'staff' ? `No.${p.staff}` : xMode === 'age' ? `${p.age}歳` : `勤続${p.tenure}年`;
        const colorOf = (g) => g === '男性' ? '#2563eb' : g === '女性' ? '#ec4899' : '#9ca3af';
        const unitLbl = xMode === 'age' ? '歳' : xMode === 'tenure' ? '年' : '';
        const clipId = 'wsclip';
        // 回帰（最小二乗）。直線＝1次、曲線＝2次（高齢/長期での上げ止まりを表現）
        const rx = shown.map(getX), ry = shown.map((p) => p.y), nN = rx.length;
        let regEl = null, regEq = null;
        if (regType === 'line' && nN >= 2) {
          const sx = rx.reduce((s, v) => s + v, 0), sy = ry.reduce((s, v) => s + v, 0);
          const sxx = rx.reduce((s, v) => s + v * v, 0), sxy = rx.reduce((s, v, i) => s + v * ry[i], 0);
          const denom = nN * sxx - sx * sx;
          const a = denom !== 0 ? (nN * sxy - sx * sy) / denom : 0;
          const b = nN ? (sy - a * sx) / nN : 0;
          regEl = <line x1={xPos(xMin)} y1={yPos(a * xMin + b)} x2={xPos(xMax)} y2={yPos(a * xMax + b)} stroke="#16a34a" strokeWidth="2" clipPath={`url(#${clipId})`} />;
          regEq = `Y = ${Math.round(a).toLocaleString('ja-JP')}X ${b >= 0 ? '+' : '−'} ${Math.abs(Math.round(b)).toLocaleString('ja-JP')}`;
        } else if (regType === 'curve') {
          const c = quadFit(rx, ry);
          if (c) {
            const pts = [];
            for (let i = 0; i <= 40; i++) { const x = xMin + (xMax - xMin) * i / 40; pts.push(`${xPos(x)},${yPos(c[0] + c[1] * x + c[2] * x * x)}`); }
            regEl = <polyline points={pts.join(' ')} fill="none" stroke="#16a34a" strokeWidth="2" clipPath={`url(#${clipId})`} />;
            regEq = `Y = ${c[2].toFixed(1)}X² ${c[1] >= 0 ? '+' : '−'} ${Math.abs(c[1]).toFixed(0)}X ${c[0] >= 0 ? '+' : '−'} ${Math.abs(Math.round(c[0])).toLocaleString('ja-JP')}`;
          }
        }
        return (
          <div ref={chartRef} className="bg-white rounded-xl shadow p-6">
            <div className="flex flex-wrap items-center gap-4 mb-3 text-[11px] text-gray-600">
              <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-full" style={{ background: '#2563eb' }} />男性</span>
              <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-full" style={{ background: '#ec4899' }} />女性</span>
              <span className="flex items-center gap-1"><span className="inline-block w-4 border-t-2 border-dashed" style={{ borderColor: '#9333ea' }} />平均 ¥{fmtY(avgY)}</span>
              {regEq && <span className="flex items-center gap-1"><span className="inline-block w-4 border-t-2" style={{ borderColor: '#16a34a' }} />{regType === 'curve' ? '回帰曲線' : '回帰直線'}</span>}
              {regEq && <span className="text-gray-500">{regEq}{unitLbl && <span className="text-gray-400">（X＝{unitLbl}）</span>}</span>}
            </div>
            <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ maxWidth: '100%' }}>
              <defs><clipPath id={clipId}><rect x={padL} y={padT} width={plotW} height={plotH} /></clipPath></defs>
              {axis}
              {xTicks.map((t) => (
                <text key={`x${t}`} x={xPos(t)} y={H - padB + 16} fontSize="10" fill="#9ca3af" textAnchor="middle">{t}</text>
              ))}
              <text x={padL + plotW / 2} y={H - 6} fontSize="10" fill="#6b7280" textAnchor="middle">{X_MODES[xMode]}</text>
              {avgLine}
              {regEl}
              {plotted.map((p, i) => (
                <circle key={i} cx={xPos(p.px)} cy={yPos(p.y)} r="6" fill={colorOf(p.gender)} fillOpacity="0.85" stroke="#ffffff" strokeWidth="0.6">
                  <title>{`${p.name}（${p.gender || '—'}）：${tip(p)} / ${fmtY(p.y)}円`}</title>
                </circle>
              ))}
            </svg>
          </div>
        );
      })()}

      {customerId && !loading && shown.length > 0 && (
        <div className="bg-white rounded-xl shadow p-6 mt-6">
          <div className="flex items-center flex-wrap gap-2 mb-3">
            <h3 className="font-bold text-gray-800">この散布図のAI分析</h3>
            {aiGen && <span className="text-[11px] text-gray-400">生成日時：{new Date(aiGen).toLocaleString('ja-JP')}（保存済み）</span>}
            <button onClick={runAi} disabled={aiLoading}
              className="ml-auto bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-indigo-700 disabled:opacity-50">
              {aiLoading ? 'AI分析中…（数十秒）' : aiEval ? '再分析' : 'AIで分析'}
            </button>
          </div>
          {aiErr && <div className="text-sm text-rose-600 bg-rose-50 rounded-lg px-4 py-2 mb-3">{aiErr}</div>}
          {!aiEval && !aiLoading && !aiErr && <p className="text-sm text-gray-400">「AIで分析」を押すと、この散布図（賃金×年齢/勤続・男女）をAIが分析します。結果は保存され、次回も表示されます。</p>}
          {aiEval && (
            <div className="space-y-4 text-sm">
              {aiEval.overview && <p className="text-gray-700 bg-indigo-50 rounded-lg px-4 py-3">{aiEval.overview}</p>}
              <div className="grid md:grid-cols-3 gap-3">
                <div className="bg-emerald-50 rounded-xl p-4"><div className="font-bold text-emerald-700 mb-2">特徴・良い点</div><ul className="list-disc pl-5 space-y-1 text-gray-700">{(aiEval.findings || []).map((t, i) => <li key={i}>{t}</li>)}</ul></div>
                <div className="bg-rose-50 rounded-xl p-4"><div className="font-bold text-rose-700 mb-2">注意点・リスク</div><ul className="list-disc pl-5 space-y-1 text-gray-700">{(aiEval.risks || []).map((t, i) => <li key={i}>{t}</li>)}</ul></div>
                <div className="bg-amber-50 rounded-xl p-4"><div className="font-bold text-amber-700 mb-2">改善提案</div><ul className="list-disc pl-5 space-y-1 text-gray-700">{(aiEval.suggestions || []).map((t, i) => <li key={i}>{t}</li>)}</ul></div>
              </div>
              <p className="text-[11px] text-gray-400">※ AI（Claude）による自動生成です。最終判断はご自身で行ってください。</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const WageAnalysis = ({ customers, viewAs, customerId = '' }) => {
  const [emps, setEmps] = useState([]);
  const [rows, setRows] = useState([]);
  const [wage, setWage] = useState(null);
  const [empKey, setEmpKey] = useState('');
  const [jobCode, setJobCode] = useState('');
  const [cmp1, setCmp1] = useState('');
  const [cmp2, setCmp2] = useState('');
  const [mode, setMode] = useState('emp'); // 'emp'=社員別 / 'job'=職種別
  const [jobSel, setJobSel] = useState(''); // 職種別モードで選択中の職種
  const [loading, setLoading] = useState(false);
  const vq = viewAs ? `&view_as=${encodeURIComponent(viewAs)}` : '';

  const cust = customers.find((c) => c.id === customerId);
  const region = (cust && cust.region) || '';

  // 派遣賃金データ（最新年度）
  useEffect(() => {
    adminFetch(`/api/admin-report.php?action=wage_years&t=${Date.now()}`)
      .then((r) => r.json())
      .then((ys) => {
        const y = Array.isArray(ys) && ys.length ? ys[0].year : '';
        if (!y) { setWage(null); return null; }
        return adminFetch(`/api/admin-report.php?action=wage_data&year=${encodeURIComponent(y)}&t=${Date.now()}`)
          .then((r) => r.json()).then((d) => setWage(d && typeof d === 'object' ? d : null));
      })
      .catch(() => setWage(null));
  }, []);

  // 顧客の社員・取込データ・比較地域（保存済み）
  useEffect(() => {
    if (!customerId) { setEmps([]); setRows([]); setEmpKey(''); setJobCode(''); setJobSel(''); setCmp1(''); setCmp2(''); return; }
    setLoading(true);
    Promise.all([
      adminFetch(`/api/admin-report.php?action=employees&customer=${encodeURIComponent(customerId)}&t=${Date.now()}${vq}`).then((r) => r.json()).catch(() => []),
      adminFetch(`/api/admin-report.php?action=import_data&customer=${encodeURIComponent(customerId)}&t=${Date.now()}${vq}`).then((r) => r.json()).catch(() => null),
      adminFetch(`/api/admin-report.php?action=sales&customer=${encodeURIComponent(customerId)}&t=${Date.now()}${vq}`).then((r) => r.json()).catch(() => null),
    ]).then(([e, imp, sal]) => {
      setEmps(Array.isArray(e) ? e : []);
      setRows(imp && Array.isArray(imp.rows) ? imp.rows : []);
      setEmpKey(''); setJobCode(''); setJobSel('');
      const wc = sal && sal.wageCompare ? sal.wageCompare : {};
      setCmp1(wc.cmp1 || ''); setCmp2(wc.cmp2 || '');
    }).finally(() => setLoading(false));
  }, [customerId, vq]);

  // 比較地域を顧客ごとに保存
  const saveCompare = (c1, c2) => {
    if (!customerId) return;
    adminFetch('/api/admin-report.php', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'save_sales', customerId, wageCompare: { cmp1: c1, cmp2: c2 }, ...(viewAs ? { view_as: viewAs } : {}) }),
    }).catch(() => {});
  };
  const changeCmp1 = (v) => { setCmp1(v); saveCompare(v, cmp2); };
  const changeCmp2 = (v) => { setCmp2(v); saveCompare(cmp1, v); };

  const emp = emps.find((e) => e.key === empKey);
  const natItems = (wage && wage.national && Array.isArray(wage.national.items)) ? wage.national.items : [];
  const prefs = (wage && wage.regional && Array.isArray(wage.regional.prefecture)) ? wage.regional.prefecture : [];
  // 保存済みの地域別最低賃金が無ければ初期値（令和7年度）にフォールバック
  const mins = (wage && Array.isArray(wage.minimum) && wage.minimum.length) ? wage.minimum : DEFAULT_MIN_WAGE;
  const prefList = prefs.map((p) => p.name).filter((n) => n && n !== '全国計' && n !== region);
  const idxOf = (name) => { const p = prefs.find((x) => x.name === name); return p && p.index != null ? Number(p.index) / 100 : null; };
  const minOf = (name) => { const m = mins.find((x) => x.name === name); return m && m.wage != null ? Number(m.wage) : null; };

  // 代表行（月平均所定労働時間のある直近月。AJは平均値のため月に依存しない）
  const repRow = (key) => {
    const mine = rows.filter((r) => jsEmpKey(r) === key);
    if (!mine.length) return null;
    const s = [...mine].sort((a, b) => monthKey(b['年月']).localeCompare(monthKey(a['年月'])));
    return s.find((r) => num(r['月平均所定労働時間']) > 0) || s[0];
  };
  // 職種別モードの職種一覧（社員の職種区分）
  const jobOptions = useMemo(() => {
    const s = new Set();
    emps.filter((e) => !e.excluded).forEach((e) => { if (e.jobClass) s.add(e.jobClass); });
    return [...s];
  }, [emps]);

  // 対象：社員別＝1人／職種別＝その職種の社員平均
  const target = useMemo(() => {
    const resolveItem = (code, nm) => {
      let it = code ? natItems.find((x) => String(x.code) === String(code)) : null;
      if (!it && nm) it = natItems.find((x) => x.name === nm);
      return it || null;
    };
    if (mode === 'emp') {
      if (!emp) return null;
      const r = repRow(emp.key);
      const code = jobCode || emp.jobClassCode || emp.jobCode || '';
      return { label: emp.name || emp.empNo, count: 1, curWage: seiteinaiMonthly(r), mph: num(r && r['月平均所定労働時間']),
        tY: tenureYears(emp.hireDate, emp.lastMonth), jobItem: resolveItem(code, emp.jobClass || emp.jobName) };
    }
    if (!jobSel) return null;
    const members = emps.filter((e) => !e.excluded && e.jobClass === jobSel);
    const ws = [], ms = [], ts = []; let code = '';
    members.forEach((e) => {
      const r = repRow(e.key); const w = seiteinaiMonthly(r); const m = num(r && r['月平均所定労働時間']); const t = tenureYears(e.hireDate, e.lastMonth);
      if (w > 0) ws.push(w); if (m > 0) ms.push(m); if (t != null) ts.push(t); if (!code) code = e.jobClassCode || e.jobCode || '';
    });
    const avg = (a) => (a.length ? a.reduce((s, v) => s + v, 0) / a.length : 0);
    return { label: `${jobSel}（${members.length}名）`, count: members.length, curWage: Math.round(avg(ws)), mph: Math.round(avg(ms)),
      tY: ts.length ? avg(ts) : null, jobItem: resolveItem(jobCode || code, jobSel) };
  }, [mode, emp, jobSel, emps, rows, jobCode, natItems]); // eslint-disable-line react-hooks/exhaustive-deps

  const jobItem = target ? target.jobItem : null;
  const mph = target ? target.mph : 0;
  const curWage = target ? target.curWage : 0;
  const tY = target ? target.tY : null;
  const nearestIdx = tY == null ? -1 : TENURE_BUCKETS.reduce((best, b, i) => (Math.abs(b.y - tY) < Math.abs(TENURE_BUCKETS[best].y - tY) ? i : best), 0);

  // 系列を構築
  const series = [];
  if (region && minOf(region) != null && mph > 0) {
    const v = minOf(region) * mph;
    series.push({ key: 'min', label: `地域別最低賃金（${region}）`, color: '#1d4ed8', values: Object.fromEntries(TENURE_BUCKETS.map((b) => [b.key, v])) });
  }
  if (jobItem && mph > 0) {
    const mk = (mult) => Object.fromEntries(TENURE_BUCKETS.map((b) => [b.key, (Number(jobItem[b.key]) || 0) * mult * mph]));
    if (region && idxOf(region) != null) series.push({ key: 'h_self', label: `派遣賃金（${region}）`, color: '#60a5fa', values: mk(idxOf(region)) });
    if (cmp1 && idxOf(cmp1) != null) series.push({ key: 'h_c1', label: `派遣賃金（${cmp1}）`, color: '#f59e0b', values: mk(idxOf(cmp1)) });
    if (cmp2 && idxOf(cmp2) != null) series.push({ key: 'h_c2', label: `派遣賃金（${cmp2}）`, color: '#fb923c', values: mk(idxOf(cmp2)) });
    series.push({ key: 'h_nat', label: '派遣賃金（全国平均）', color: '#34d399', values: mk(1) });
  }
  const allVals = series.flatMap((s) => Object.values(s.values)).concat(curWage || 0);
  const max = Math.max(1, ...allVals) * 1.1;

  return (
    <div>
      <h2 className="text-2xl font-bold mb-1">適正賃金分析</h2>
      <p className="text-xs text-gray-500 mb-6">対象社員の所定内賃金（月給換算）を、地域別最低賃金・派遣労働者賃金（顧客の地域・比較地域・全国平均）と勤続年数別に比較します。派遣賃金データの最新年度を使用します。</p>

      {!customerId && <div className="bg-white rounded-xl shadow p-8 text-center text-gray-400 mb-6">左メニュー下部の「顧客」から選択してください。</div>}
      {customerId && <div className="bg-white rounded-xl shadow p-5 mb-6 flex flex-wrap items-end gap-4">
        <div>
          <label className="block text-xs text-gray-500 mb-1">地域</label>
          <div className="text-sm text-gray-700 py-2">{region || '（顧客管理で未設定）'}</div>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">表示</label>
          <div className="flex gap-1">
            {[['emp', '社員別'], ['job', '職種別']].map(([k, l]) => (
              <button key={k} onClick={() => setMode(k)}
                className={`px-3 py-2 rounded-lg text-xs font-bold ${mode === k ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>{l}</button>
            ))}
          </div>
        </div>
        {mode === 'emp' ? (
          <div>
            <label className="block text-xs text-gray-500 mb-1">社員</label>
            <select value={empKey} onChange={(e) => setEmpKey(e.target.value)} disabled={!customerId}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm min-w-[12rem] focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100">
              <option value="">— 社員を選択 —</option>
              {emps.filter((e) => !e.excluded).map((e) => <option key={e.key} value={e.key}>{e.name || e.empNo}</option>)}
            </select>
          </div>
        ) : (
          <div>
            <label className="block text-xs text-gray-500 mb-1">職種</label>
            <select value={jobSel} onChange={(e) => setJobSel(e.target.value)} disabled={!customerId}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm min-w-[12rem] focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100">
              <option value="">— 職種を選択 —</option>
              {jobOptions.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
        )}
        <div>
          <label className="block text-xs text-gray-500 mb-1">職種区分（基準値）</label>
          <select value={jobCode || (jobItem && jobItem.code) || ''} onChange={(e) => setJobCode(e.target.value)} disabled={!wage}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm min-w-[12rem] focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100">
            <option value="">— 職種を選択 —</option>
            {natItems.map((it) => <option key={(it.code || '') + it.name} value={it.code || ''}>{it.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">比較地域1</label>
          <select value={cmp1} onChange={(e) => changeCmp1(e.target.value)} disabled={!wage}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100">
            <option value="">—</option>
            {prefList.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">比較地域2</label>
          <select value={cmp2} onChange={(e) => changeCmp2(e.target.value)} disabled={!wage}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100">
            <option value="">—</option>
            {prefList.filter((n) => n !== cmp1).map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
        {target && series.length > 0 && <button onClick={() => window.print()} className="ml-auto bg-gray-800 text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-gray-900 self-end">印刷 / PDF</button>}
      </div>}

      {!wage && <div className="bg-white rounded-xl shadow p-8 text-center text-rose-500">派遣賃金データが未登録です。「派遣賃金データ」で別添2・別添3・地域別最低賃金を登録してください。</div>}
      {loading && <div className="text-gray-500">読み込み中…</div>}
      {wage && customerId && !region && <div className="bg-white rounded-xl shadow p-6 text-amber-600 text-sm">顧客管理で「地域」を設定すると、地域別最低賃金・派遣賃金（自社地域）が表示されます。</div>}
      {wage && target && !jobItem && <div className="bg-white rounded-xl shadow p-6 text-amber-600 text-sm">職種区分が特定できません。「職種区分（基準値）」を選択するか、社員・職種で職種区分を設定してください。</div>}
      {wage && target && mph <= 0 && <div className="bg-white rounded-xl shadow p-6 text-amber-600 text-sm">「月平均所定労働時間」が取得できないため、月給換算ができません。</div>}

      {wage && target && series.length > 0 && (
        <div id="report-print" className="bg-white rounded-xl shadow p-6">
          <div className="flex items-center flex-wrap gap-3 mb-1 text-sm">
            <span className="font-bold text-gray-800">{target.label}</span>
            <span className="text-xs text-gray-500">{jobItem ? jobItem.name : ''}{region ? `／${region}` : ''}{tY != null ? `／勤続${Math.round(tY * 10) / 10}年` : ''}</span>
            <span className="ml-auto text-sm">{mode === 'job' ? '平均所定内賃金' : '現在の所定内賃金'}：<span className="font-bold text-rose-600">¥{fmtY(curWage)}</span></span>
          </div>
          <p className="text-[11px] text-gray-400 mb-4">棒＝勤続年数別の各賃金（{mph}時間/月で月給換算）。赤の点線＝{mode === 'job' ? 'この職種の平均賃金' : '対象社員の現在賃金'}。</p>
          <div className="flex flex-wrap items-center gap-3 mb-3 text-[11px] text-gray-600">
            {series.map((s) => (
              <span key={s.key} className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm" style={{ background: s.color }} />{s.label}</span>
            ))}
            <span className="flex items-center gap-1 text-rose-600"><span className="inline-block w-4 border-t-2 border-dashed border-rose-500" />現在賃金</span>
          </div>
          <div className="relative" style={{ height: 300 }}>
            <div className="absolute left-0 top-0 text-[10px] text-gray-400">¥{fmtY(max)}</div>
            {/* プロット領域：棒と現在賃金線で同じ座標系を共有 */}
            <div className="absolute left-12 right-2 top-2 bottom-6">
              <div className="absolute inset-0 flex items-end gap-2">
                {TENURE_BUCKETS.map((b) => (
                  <div key={b.key} className="flex-1 flex items-end justify-center gap-0.5 h-full">
                    {series.map((s) => (
                      <div key={s.key} title={`${s.label} ${b.label}：¥${fmtY(s.values[b.key])}`}
                        style={{ height: `${Math.min(100, s.values[b.key] / max * 100)}%`, background: s.color }} className="w-2.5 rounded-t" />
                    ))}
                  </div>
                ))}
              </div>
              {curWage > 0 && (
                <div className="absolute left-0 right-0 border-t-2 border-dashed border-rose-500 z-20" style={{ bottom: `${Math.min(100, curWage / max * 100)}%` }}>
                  <span className="absolute right-0 -top-4 text-[10px] font-bold text-rose-600 bg-white/90 px-1">¥{fmtY(curWage)}</span>
                </div>
              )}
            </div>
            <div className="absolute left-12 right-2 bottom-0 flex gap-2">
              {TENURE_BUCKETS.map((b, bi) => (
                <div key={b.key} className={`flex-1 text-center text-[10px] ${bi === nearestIdx ? 'font-bold text-rose-600' : 'text-gray-500'}`}>{b.label}{bi === nearestIdx ? '（現在）' : ''}</div>
              ))}
            </div>
          </div>

          <div className="overflow-auto mt-6">
            <table className="w-full text-xs">
              <thead><tr className="text-left text-gray-500 border-b border-gray-200">
                <th className="py-2 pr-3">区分</th>{TENURE_BUCKETS.map((b) => <th key={b.key} className="text-right px-2">{b.label}</th>)}
              </tr></thead>
              <tbody>
                {series.map((s) => (
                  <tr key={s.key} className="border-b border-gray-50">
                    <td className="py-1.5 pr-3"><span className="inline-block w-2.5 h-2.5 rounded-sm mr-1 align-middle" style={{ background: s.color }} />{s.label}</td>
                    {TENURE_BUCKETS.map((b) => <td key={b.key} className="text-right px-2 tabular-nums">¥{fmtY(s.values[b.key])}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// ====================== 印刷（選んだレポートをまとめて1回で出力） ======================
const PRINT_ITEMS = [
  { key: 'monthly', label: 'マンスリー分析レポート' },
  { key: 'workforce', label: '組織人員構成分析' },
  { key: 'labor', label: '人時生産性分析' },
  { key: 'profit', label: '人件費予実管理' },
  { key: 'scatter', label: '賃金散布図' },
];
const PrintCenter = ({ customers, viewAs, customerId, hideProfit = false }) => {
  const [checked, setChecked] = useState({ monthly: true, workforce: true, labor: true, profit: false, scatter: true });
  const areaRef = useRef(null);
  const items = PRINT_ITEMS.filter((i) => !(hideProfit && i.key === 'profit'));
  const toggle = (k) => setChecked((p) => ({ ...p, [k]: !p[k] }));
  const anyChecked = items.some((i) => checked[i.key]);

  // プレビュー領域をアプリのCSSごと印刷用iframeへ複製して1回の印刷にまとめる
  const doPrint = () => {
    const area = areaRef.current;
    if (!area) return;
    // Reactのcontrolled inputは属性にvalueを持たないため、現在値を属性へ反映してから複製
    area.querySelectorAll('input').forEach((el) => el.setAttribute('value', el.value));
    const styles = Array.from(document.querySelectorAll('link[rel="stylesheet"], style')).map((el) => el.outerHTML).join('');
    const override = '<style>'
      + 'button,select{display:none !important;}'                                     // 操作類は出力しない
      + 'input{border:none !important;background:transparent !important;box-shadow:none !important;}' // 入力値は文字として残す
      + '.print-section{break-after:page;}.print-section:last-child{break-after:auto;}' // レポートごとに改ページ
      + 'body{background:#fff;margin:0;padding:8mm;}'
      + '@media print{body *{visibility:visible !important;}#report-print{position:static !important;padding:0 !important;}}' // 単体印刷用CSSの打ち消し
      + '</style>';
    const html = `<!doctype html><html lang="ja"><head><meta charset="utf-8">${styles}${override}</head><body>${area.innerHTML}</body></html>`;
    printHtml(html, 800);
  };

  return (
    <div>
      <h2 className="text-2xl font-bold mb-1">印刷</h2>
      <p className="text-xs text-gray-500 mb-6">出力するレポートにチェックして「選択したレポートを印刷」を押すと、選んだものだけを1つにまとめて印刷（PDF保存）できます。対象月・年度・横軸などは下のプレビュー内で変更できます。</p>
      {!customerId && <div className="bg-white rounded-xl shadow p-8 text-center text-gray-400">左メニュー下部の「顧客」から選択してください。</div>}
      {customerId && (
        <>
          <div className="bg-white rounded-xl shadow p-5 mb-2 flex flex-wrap items-center gap-4 sticky top-0 z-20">
            {items.map((i) => (
              <label key={i.key} className="flex items-center gap-1.5 text-sm text-gray-700 cursor-pointer">
                <input type="checkbox" checked={!!checked[i.key]} onChange={() => toggle(i.key)} className="w-4 h-4 accent-pink-dark" />
                {i.label}
              </label>
            ))}
            <button onClick={doPrint} disabled={!anyChecked}
              className="ml-auto bg-pink-dark text-white px-5 py-2 rounded-lg text-sm font-bold hover:bg-brown disabled:opacity-40">選択したレポートを印刷</button>
          </div>
          <div ref={areaRef}>
            {checked.monthly && <div className="print-section border-t-4 border-pink-beige pt-6 mt-6"><MonthlyView customers={customers} viewAs={viewAs} customerId={customerId} /></div>}
            {checked.workforce && <div className="print-section border-t-4 border-pink-beige pt-6 mt-6"><WorkforceAnalysis customers={customers} viewAs={viewAs} customerId={customerId} /></div>}
            {checked.labor && <div className="print-section border-t-4 border-pink-beige pt-6 mt-6"><LaborCostManager customers={customers} viewAs={viewAs} customerId={customerId} /></div>}
            {!hideProfit && checked.profit && <div className="print-section border-t-4 border-pink-beige pt-6 mt-6"><LaborCostManager customers={customers} viewAs={viewAs} customerId={customerId} title="人件費予実管理" profit /></div>}
            {checked.scatter && <div className="print-section border-t-4 border-pink-beige pt-6 mt-6"><WageScatter customers={customers} viewAs={viewAs} customerId={customerId} /></div>}
          </div>
        </>
      )}
    </div>
  );
};

const ReportAdmin = ({ page = 'report_monthly', viewAs = '', customerId = '' }) => {
  const [customers, setCustomers] = useState([]);
  const role = localStorage.getItem('admin_role');
  const myEmail = (localStorage.getItem('admin_email') || '').toLowerCase();
  const realIsPartner = role === 'partner_sharoushi';
  const scoped = realIsPartner || !!viewAs; // 担当顧客のみに制限された表示か
  const effectivePartner = realIsPartner ? myEmail : (viewAs || '');
  const vq = viewAs ? `&view_as=${encodeURIComponent(viewAs)}` : '';

  const reload = useCallback(() => {
    adminFetch(`/api/admin-report.php?action=customers&t=${Date.now()}${vq}`)
      .then((r) => r.json()).then((d) => setCustomers(Array.isArray(d) ? d : [])).catch(() => setCustomers([]));
  }, [vq]);
  useEffect(() => { reload(); }, [reload]);

  if (page === 'report_customers') return <CustomerManager customers={customers} reload={reload} isPartner={scoped} myEmail={effectivePartner} viewAs={viewAs} />;
  if (page === 'report_import') return <CsvImport customers={customers} viewAs={viewAs} reload={reload} customerId={customerId} />;
  if (page === 'report_monthly') return <MonthlyView customers={customers} viewAs={viewAs} customerId={customerId} />;
  if (page === 'report_wage') return <WageDataAdmin />;
  if (page === 'report_workforce') return <WorkforceAnalysis customers={customers} viewAs={viewAs} customerId={customerId} />;
  if (page === 'report_labor_cost') return <LaborCostManager customers={customers} viewAs={viewAs} customerId={customerId} />;
  if (page === 'report_labor_profit') return <LaborCostManager customers={customers} viewAs={viewAs} customerId={customerId} title="人件費予実管理" profit />;
  if (page === 'report_wage_analysis') return <WageAnalysis customers={customers} viewAs={viewAs} customerId={customerId} />;
  if (page === 'report_wage_scatter') return <WageScatter customers={customers} viewAs={viewAs} customerId={customerId} />;
  if (page === 'report_print') return <PrintCenter customers={customers} viewAs={viewAs} customerId={customerId} hideProfit={scoped} />;
  if (page === 'report_min_wage') return <MinWageAdmin />;
  return null;
};

export default ReportAdmin;
